import { useState, useEffect, useCallback, useMemo, Fragment } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { ClickableAvatar } from "../components/ClickableAvatar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { DeleteConfirmModal } from "../components/DeleteConfirmModal";
import { HardwareExpiry, TurnstileStatus } from "../components/biometrics/ReconcileStatusCells";
import { PersonSearchPicker } from "../components/biometrics/PersonSearchPicker";
import { DEFAULT_SUFFIX_DIGITS, MatchMethodBadge, MatchMethodFields } from "../components/biometrics/DeviceMatchMethod";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Building2,
  UserPlus,
  CalendarCheck,
  Calendar as CalendarIcon,
  Download,
  Copy,
  ShieldCheck,
  QrCode,
  Radio,
  Fingerprint,
  RefreshCw,
  Cpu,
  HelpCircle,
  Check,
  Search,
  Wifi,
  ExternalLink,
  Terminal,
  Eye,
  FileCode,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Clock,
  UserCheck,
  Users,
  ShieldAlert,
  AlertTriangle,
  CloudDownload,
  UploadCloud,
  Link as LinkIcon,
  Key,
  Globe,
  Loader2,
} from "lucide-react";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";


// Resolves backend API URL (for Webhooks that external biometric hardware/software sends HTTP requests to)
const resolveBackendUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  let backendBase = process.env.REACT_APP_BACKEND_URL;
  if (!backendBase) {
    backendBase = window.location.origin;
  }
  const cleanBase = backendBase.replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
};

// Resolves frontend URL (for user-facing UI links: QR codes, public signup, counter check-in)
const resolveFrontendUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) {
    try {
      const parsed = new URL(url);
      const backendUrlStr = process.env.REACT_APP_BACKEND_URL;
      const backendHost = backendUrlStr ? new URL(backendUrlStr).host : null;
      if (backendHost && parsed.host === backendHost) {
        return `${window.location.origin}${parsed.pathname}${parsed.search}`;
      }
      return url;
    } catch {
      return url;
    }
  }
  return `${window.location.origin}${url.startsWith("/") ? "" : "/"}${url}`;
};

// Helper for biometric device connection status
const getConnectionStatus = (device) => {
  if (device.status === "Disabled") {
    return {
      label: "Disabled",
      badgeClass: "bg-muted text-muted-foreground border-border",
      dotClass: "bg-muted-foreground",
      timeText: "Disabled",
      isOnline: false,
    };
  }
  if (!device.lastPunchAt) {
    return {
      label: "Awaiting Punch",
      badgeClass: "bg-sky-500/10 text-sky-400 border-sky-500/20",
      dotClass: "bg-sky-400",
      timeText: "Ready for punch",
      isOnline: false,
    };
  }
  const lastPunch = new Date(device.lastPunchAt);
  const now = new Date();
  const diffMinutes = Math.floor((now - lastPunch) / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 15) {
    return {
      label: "Online",
      badgeClass: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      dotClass: "bg-emerald-400 animate-pulse",
      timeText: diffMinutes <= 1 ? "Just now" : `${diffMinutes}m ago`,
      isOnline: true,
    };
  } else if (diffHours < 24) {
    return {
      label: "Active Today",
      badgeClass: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
      dotClass: "bg-emerald-400",
      timeText: `${diffHours}h ago`,
      isOnline: true,
    };
  } else if (diffDays <= 7) {
    return {
      label: "Idle",
      badgeClass: "bg-amber-500/15 text-amber-400 border-amber-500/30",
      dotClass: "bg-amber-400",
      timeText: `${diffDays}d ago`,
      isOnline: false,
    };
  } else {
    return {
      label: "Inactive",
      badgeClass: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",
      dotClass: "bg-zinc-400",
      timeText: lastPunch.toLocaleDateString("en-IN", { month: "short", day: "numeric" }),
      isOnline: false,
    };
  }
};

export default function Branches() {
  const { setBranches, fetchUser, organization, user, currentBranch } = useAuth();
  const [branchList, setBranchList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", address: "", phone: "", allowPublicSignup: false });
  const [qrData, setQrData] = useState(null);
  const [qrBranch, setQrBranch] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, branch: null });
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Query parameters for Main Tabs ("branches" | "biometrics") and Subtabs ("devices" | "punches" | "raw")
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const mainTab = searchParams.get("tab") === "biometrics" ? "biometrics" : "branches";
  const rawSubTab = searchParams.get("subtab");
  const bioActiveSubTab = (rawSubTab === "punches" || rawSubTab === "raw" || rawSubTab === "reconciliation" || rawSubTab === "users" || rawSubTab === "sync-logs") ? (rawSubTab === "users" ? "reconciliation" : rawSubTab) : "devices";

  const setMainTab = (val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (val === "biometrics") {
        next.set("tab", "biometrics");
      } else {
        next.delete("tab");
        next.delete("subtab");
      }
      return next;
    }, { replace: true });
  };

  const setBioActiveSubTab = (sub) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", "biometrics");
      if (sub === "devices") {
        next.delete("subtab");
      } else {
        next.set("subtab", sub);
      }
      return next;
    }, { replace: true });
  };

  // Global Biometric devices state
  const [allDevices, setAllDevices] = useState([]);
  const [allDevicesLoading, setAllDevicesLoading] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState("");
  const [selectedBranchFilter, setSelectedBranchFilter] = useState("all");
  const [selectedStatusFilter, setSelectedStatusFilter] = useState("all");

  // Device User Reconciliation & Expiry Sync state
  const [reconcileDeviceId, setReconcileDeviceId] = useState("");

  const getMainDevice = useCallback((devices) => {
    if (!devices || !devices.length) return "";
    const mainDev = devices.find(
      (d) =>
        d.branchName?.trim().toLowerCase() === "main" ||
        d.branchName?.trim().toLowerCase().includes("main") ||
        d.name?.trim().toLowerCase().includes("main")
    );
    if (mainDev) return mainDev.id;
    if (currentBranch?.id) {
      const curDev = devices.find((d) => d.branchId === currentBranch.id);
      if (curDev) return curDev.id;
    }
  }, [currentBranch]);

  const getActiveBranchId = useCallback((devId = reconcileDeviceId) => {
    const targetId = devId || getMainDevice(allDevices);
    const dev = allDevices.find((d) => d.id === targetId);
    return dev?.branchId || "";
  }, [reconcileDeviceId, allDevices, getMainDevice]);
  const [reconcileData, setReconcileData] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcilePulling, setReconcilePulling] = useState(false);
  const [reconcileSyncing, setReconcileSyncing] = useState(false);
  const [singleSyncingCode, setSingleSyncingCode] = useState(null);
  const [reconcileSearch, setReconcileSearch] = useState("");
  const [reconcileFilter, setReconcileFilter] = useState("all");
  const [reconcilePage, setReconcilePage] = useState(1);
  const [reconcilePageSize, setReconcilePageSize] = useState(25);
  const [mappingModalOpen, setMappingModalOpen] = useState(false);
  const [mappingCode, setMappingCode] = useState("");
  const [mappingDeviceUserName, setMappingDeviceUserName] = useState("");
  const [mappingTargetType, setMappingTargetType] = useState("member");
  const [mappingTargetId, setMappingTargetId] = useState("");
  const [mappingSaving, setMappingSaving] = useState(false);
  const [connTesting, setConnTesting] = useState(false);
  const [extendModalOpen, setExtendModalOpen] = useState(false);
  const [extendDate, setExtendDate] = useState("");
  const [extendScope, setExtendScope] = useState("all");
  const [selectedExtendMemberIds, setSelectedExtendMemberIds] = useState([]);
  const [extendReason, setExtendReason] = useState("Temporary Extension");
  const [extendSubmitting, setExtendSubmitting] = useState(false);

  // Global Biometric Logs state
  const [globalPunchLogs, setGlobalPunchLogs] = useState([]);
  const [punchTotal, setPunchTotal] = useState(0);
  const [punchPage, setPunchPage] = useState(1);
  const [punchPageSize, setPunchPageSize] = useState(50);

  const [rawWebhookLogs, setRawWebhookLogs] = useState([]);
  const [rawTotal, setRawTotal] = useState(0);
  const [rawPage, setRawPage] = useState(1);
  const [rawPageSize, setRawPageSize] = useState(50);

  const [globalLogsLoading, setGlobalLogsLoading] = useState(false);
  const [selectedRawLog, setSelectedRawLog] = useState(null);
  const [logDeleteModal, setLogDeleteModal] = useState({
    open: false,
    type: "", // "clear_raw" | "single_raw" | "clear_punches" | "single_punch"
    target: null,
  });
  const [logDeleteLoading, setLogDeleteLoading] = useState(false);

  // Branch-specific Biometric Dialog state
  const [biometricBranch, setBiometricBranch] = useState(null);
  const [devicesList, setDevicesList] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [punchLogs, setPunchLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [activeBioTab, setActiveBioTab] = useState("setup");
  const [selectedBrand, setSelectedBrand] = useState("essl");
  const [copiedWebhook, setCopiedWebhook] = useState(false);
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [deviceForm, setDeviceForm] = useState({
    name: "",
    serialNumber: "",
    provider: "essl",
    directionMode: "AUTO",
    password: "",
    notes: "",
    branchId: "",
    matchMethod: "NONE",
    phoneSuffixDigits: DEFAULT_SUFFIX_DIGITS,
    apiUrl: "",
    apiUsername: "admin",
    apiPassword: "",
    locationCode: "1",
    syncOnCreate: false,
    syncOnUpdate: false,
    syncOnDelete: false,
  });
  const [deviceSaving, setDeviceSaving] = useState(false);

  // Outgoing Biometric Sync Logs state
  const [syncLogs, setSyncLogs] = useState([]);
  const [syncLogsTotal, setSyncLogsTotal] = useState(0);
  const [syncLogsLoading, setSyncLogsLoading] = useState(false);
  const [syncLogsPage, setSyncLogsPage] = useState(1);
  const [syncLogsPageSize, setSyncLogsPageSize] = useState(50);
  const [syncLogsSearch, setSyncLogsSearch] = useState("");
  const [syncLogsStatusFilter, setSyncLogsStatusFilter] = useState("");
  const [debugSyncLog, setDebugSyncLog] = useState(null);

  const orgId = user?.organizationId || organization?.id || "";

  useEffect(() => {
    fetchBranches();
    fetchAllDevices();
    fetchBiometricLogs();
  }, []);

  const fetchBranches = async () => {
    try {
      const res = await api.get("/branches");
      setBranchList(res.data.branches || []);
      setBranches(res.data.branches || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllDevices = async (silent = false) => {
    if (!silent) setAllDevicesLoading(true);
    try {
      const res = await api.get("/attendance/devices");
      const devs = res.data.devices || [];
      setAllDevices(devs);
      if (!reconcileDeviceId && devs.length > 0) {
        const defId = getMainDevice(devs);
        if (defId) {
          setReconcileDeviceId(defId);
          const dev = devs.find((d) => d.id === defId);
          if (dev?.branchId) {
            fetchBiometricLogs(dev.branchId);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch all biometric devices:", err);
    } finally {
      if (!silent) setAllDevicesLoading(false);
    }
  };

  const fetchPunchLogs = async (
    page = punchPage,
    pageSize = punchPageSize,
    silent = false,
    branchId = getActiveBranchId()
  ) => {
    if (!silent) setGlobalLogsLoading(true);
    try {
      const branchQuery = branchId ? `&branchId=${encodeURIComponent(branchId)}` : "";
      const res = await api.get(`/attendance/biometric-logs?page=${page}&pageSize=${pageSize}${branchQuery}`);
      setGlobalPunchLogs(res.data.logs || []);
      setPunchTotal(res.data.total ?? (res.data.logs || []).length);
    } catch (err) {
      console.error("Failed to fetch punch logs:", err);
    } finally {
      if (!silent) setGlobalLogsLoading(false);
    }
  };

  const fetchRawLogs = async (
    page = rawPage,
    pageSize = rawPageSize,
    silent = false,
    branchId = getActiveBranchId()
  ) => {
    if (!silent) setGlobalLogsLoading(true);
    try {
      const branchQuery = branchId ? `&branchId=${encodeURIComponent(branchId)}` : "";
      const res = await api.get(`/attendance/biometric-raw-logs?page=${page}&pageSize=${pageSize}${branchQuery}`);
      setRawWebhookLogs(res.data.rawLogs || []);
      setRawTotal(res.data.total ?? (res.data.rawLogs || []).length);
    } catch (err) {
      console.error("Failed to fetch raw biometric logs:", err);
    } finally {
      if (!silent) setGlobalLogsLoading(false);
    }
  };

  const fetchBiometricLogs = async (branchId = getActiveBranchId()) => {
    setGlobalLogsLoading(true);
    try {
      const branchQuery = branchId ? `&branchId=${encodeURIComponent(branchId)}` : "";
      const [resPunches, resRaw] = await Promise.all([
        api.get(`/attendance/biometric-logs?page=${punchPage}&pageSize=${punchPageSize}${branchQuery}`),
        api.get(`/attendance/biometric-raw-logs?page=${rawPage}&pageSize=${rawPageSize}${branchQuery}`),
      ]);
      setGlobalPunchLogs(resPunches.data.logs || []);
      setPunchTotal(resPunches.data.total ?? (resPunches.data.logs || []).length);
      setRawWebhookLogs(resRaw.data.rawLogs || []);
      setRawTotal(resRaw.data.total ?? (resRaw.data.rawLogs || []).length);
    } catch (err) {
      console.error("Failed to fetch biometric logs:", err);
    } finally {
      setGlobalLogsLoading(false);
    }
  };

  const fetchSyncLogs = async (
    page = syncLogsPage,
    pageSize = syncLogsPageSize,
    search = syncLogsSearch,
    status = syncLogsStatusFilter,
    silent = false,
    branchId = getActiveBranchId()
  ) => {
    if (!silent) setSyncLogsLoading(true);
    try {
      const branchQuery = branchId ? `&branchId=${encodeURIComponent(branchId)}` : "";
      const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
      const statusQuery = status ? `&status=${encodeURIComponent(status)}` : "";
      const res = await api.get(
        `/attendance/biometric-sync-logs?page=${page}&pageSize=${pageSize}${branchQuery}${searchQuery}${statusQuery}`
      );
      setSyncLogs(res.data.logs || []);
      setSyncLogsTotal(res.data.total ?? (res.data.logs || []).length);
    } catch (err) {
      console.error("Failed to fetch biometric sync logs:", err);
    } finally {
      if (!silent) setSyncLogsLoading(false);
    }
  };

  const handleRefreshAllBiometrics = () => {
    fetchAllDevices();
    const branchId = getActiveBranchId();
    if (bioActiveSubTab === "raw") {
      fetchRawLogs(rawPage, rawPageSize, false, branchId);
    } else if (bioActiveSubTab === "punches") {
      fetchPunchLogs(punchPage, punchPageSize, false, branchId);
    } else if (bioActiveSubTab === "reconciliation") {
      const devId = reconcileDeviceId || getMainDevice(allDevices);
      if (devId) fetchReconciliation(devId);
    } else if (bioActiveSubTab === "sync-logs") {
      fetchSyncLogs(syncLogsPage, syncLogsPageSize, syncLogsSearch, syncLogsStatusFilter, false, branchId);
    } else {
      fetchBiometricLogs(branchId);
    }
  };

  const handleRawPageChange = (newPage) => {
    setRawPage(newPage);
    fetchRawLogs(newPage, rawPageSize, false, getActiveBranchId());
  };

  const handleRawPageSizeChange = (newSizeStr) => {
    const newSize = parseInt(newSizeStr, 10);
    setRawPageSize(newSize);
    setRawPage(1);
    fetchRawLogs(1, newSize, false, getActiveBranchId());
  };

  const handlePunchPageChange = (newPage) => {
    setPunchPage(newPage);
    fetchPunchLogs(newPage, punchPageSize, false, getActiveBranchId());
  };

  const handlePunchPageSizeChange = (newSizeStr) => {
    const newSize = parseInt(newSizeStr, 10);
    setPunchPageSize(newSize);
    setPunchPage(1);
    fetchPunchLogs(1, newSize, false, getActiveBranchId());
  };

  const handleDeviceBranchChange = (val) => {
    setReconcileDeviceId(val);
    const branchId = getActiveBranchId(val);
    if (bioActiveSubTab === "reconciliation") {
      fetchReconciliation(val);
    } else if (bioActiveSubTab === "punches") {
      setPunchPage(1);
      fetchPunchLogs(1, punchPageSize, false, branchId);
    } else if (bioActiveSubTab === "raw") {
      setRawPage(1);
      fetchRawLogs(1, rawPageSize, false, branchId);
    }
  };

  const handleDeleteLogsConfirm = async () => {
    setLogDeleteLoading(true);
    try {
      const activeBranchId = getActiveBranchId();
      if (logDeleteModal.type === "clear_raw") {
        const res = await api.delete("/attendance/biometric-raw-logs");
        toast.success(res.data.message || "All raw webhook logs cleared");
        setRawPage(1);
        fetchRawLogs(1, rawPageSize, false, activeBranchId);
      } else if (logDeleteModal.type === "single_raw") {
        await api.delete(`/attendance/biometric-raw-logs/${logDeleteModal.target.id}`);
        toast.success("Webhook payload deleted");
        if (selectedRawLog?.id === logDeleteModal.target.id) {
          setSelectedRawLog(null);
        }
        fetchRawLogs(rawPage, rawPageSize, false, activeBranchId);
      } else if (logDeleteModal.type === "clear_punches") {
        const res = await api.delete("/attendance/biometric-logs");
        toast.success(res.data.message || "All punch logs cleared");
        setPunchPage(1);
        fetchPunchLogs(1, punchPageSize, false, activeBranchId);
      } else if (logDeleteModal.type === "single_punch") {
        await api.delete(`/attendance/biometric-logs/${logDeleteModal.target.id}`);
        toast.success("Punch log deleted");
        fetchPunchLogs(punchPage, punchPageSize, false, activeBranchId);
      }
      setLogDeleteModal({ open: false, type: "", target: null });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete log(s)");
    } finally {
      setLogDeleteLoading(false);
    }
  };


  const handleSave = async () => {
    if (!form.name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/branches/${editing.id}`, form);
        toast.success("Branch updated");
      } else {
        await api.post("/branches", form);
        toast.success("Branch created");
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", address: "", phone: "", allowPublicSignup: false });
      fetchBranches();
      fetchUser();
      fetchAllDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteModal.branch) return;
    setDeleteLoading(true);
    try {
      await api.delete(`/branches/${deleteModal.branch.id}`);
      toast.success(`"${deleteModal.branch.name}" has been removed`);
      setDeleteModal({ open: false, branch: null });
      fetchBranches();
      fetchAllDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleQR = async (branch, qrType = "attendance") => {
    try {
      const frontendOrigin = window.location.origin;
      const res = await api.get(
        `/branches/${branch.id}/qr?qr_type=${qrType}&frontend_url=${encodeURIComponent(frontendOrigin)}`
      );
      setQrData(res.data);
      setQrBranch(branch);
    } catch {
      toast.error("Failed to generate QR");
    }
  };

  const downloadQR = () => {
    if (!qrData) return;
    const link = document.createElement("a");
    link.download = `${qrBranch?.name || "branch"}-${qrData.type}-qr.png`;
    link.href = qrData.qrCode;
    link.click();
  };

  const copyUrl = () => {
    if (!qrData) return;
    navigator.clipboard.writeText(resolveFrontendUrl(qrData.url));
    toast.success("URL copied to clipboard");
  };

  // Branch Biometric Modal Handlers
  const handleBiometric = async (branch) => {
    setBiometricBranch(branch);
    setActiveBioTab("setup");
    setShowAddDevice(false);
    setEditingDevice(null);
    setDeviceForm((p) => ({ ...p, branchId: branch.id }));
    fetchBranchDevices(branch.id);
    fetchBranchPunchLogs(branch.id);
  };

  const handleSwitchBiometricBranch = (branch) => {
    setBiometricBranch(branch);
    setShowAddDevice(false);
    setEditingDevice(null);
    setDeviceForm((p) => ({ ...p, branchId: branch.id }));
    fetchBranchDevices(branch.id);
    fetchBranchPunchLogs(branch.id);
  };

  const handleOpenAddBiometricGlobal = (preselectedBranch = null) => {
    let target = preselectedBranch;
    if (!target) {
      if (selectedBranchFilter !== "all") {
        target = branchList.find((b) => b.id === selectedBranchFilter);
      }
      if (!target) {
        target = branchList.find((b) => b.isDefault) || branchList[0];
      }
    }
    if (!target) {
      toast.error("Please add a branch first before adding biometric devices");
      return;
    }
    setBiometricBranch(target);
    setActiveBioTab("setup");
    setEditingDevice(null);
    setDeviceForm({
      name: "",
      serialNumber: "",
      provider: selectedBrand,
      directionMode: "AUTO",
      password: "",
      notes: "",
      branchId: target.id,
      matchMethod: "NONE",
      phoneSuffixDigits: DEFAULT_SUFFIX_DIGITS,
      apiUrl: "",
      apiUsername: "admin",
      apiPassword: "",
      locationCode: "1",
    });
    setShowAddDevice(true);
    fetchBranchDevices(target.id);
    fetchBranchPunchLogs(target.id);
  };

  const fetchBranchDevices = async (branchId, silent = false) => {
    if (!silent) setDevicesLoading(true);
    try {
      const res = await api.get(`/attendance/devices?branchId=${branchId}`);
      setDevicesList(res.data.devices || []);
    } catch (err) {
      console.error(err);
    } finally {
      if (!silent) setDevicesLoading(false);
    }
  };

  const fetchBranchPunchLogs = async (branchId) => {
    setLogsLoading(true);
    try {
      const res = await api.get(`/attendance/biometric-logs?branchId=${branchId}&pageSize=50`);
      setPunchLogs(res.data.logs || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLogsLoading(false);
    }
  };

  const copyWebhookUrl = () => {
    if (!biometricBranch) return;
    const url = resolveBackendUrl(`/api/attendance/webhook/${selectedBrand}/${orgId}/${biometricBranch.id}`);
    navigator.clipboard.writeText(url);
    setCopiedWebhook(true);
    toast.success("Branch Webhook URL copied to clipboard!");
    setTimeout(() => setCopiedWebhook(false), 2000);
  };

  const handleOpenAddDevice = () => {
    setEditingDevice(null);
    setDeviceForm({
      name: "",
      serialNumber: "",
      provider: selectedBrand,
      directionMode: "AUTO",
      password: "",
      notes: "",
      branchId: biometricBranch?.id || "",
      matchMethod: "NONE",
      phoneSuffixDigits: DEFAULT_SUFFIX_DIGITS,
      apiUrl: "",
      apiUsername: "admin",
      apiPassword: "",
      locationCode: "1",
      syncOnCreate: false,
      syncOnUpdate: false,
      syncOnDelete: false,
    });
    setShowAddDevice(true);
  };

  const handleOpenEditDevice = (device, targetBranch = null) => {
    if (editingDevice?.id === device.id) {
      setEditingDevice(null);
      return;
    }

    const branch =
      targetBranch ||
      branchList.find((b) => b.id === device.branchId) ||
      biometricBranch ||
      branchList[0];

    if (branch && branch.id !== biometricBranch?.id) {
      setBiometricBranch(branch);
      fetchBranchDevices(branch.id);
      fetchBranchPunchLogs(branch.id);
    }
    setActiveBioTab("setup");
    setEditingDevice(device);
    setShowAddDevice(false);
    setDeviceForm({
      name: device.name || "",
      serialNumber: device.serialNumber || "",
      provider: device.provider || selectedBrand,
      directionMode: device.directionMode || "AUTO",
      password: device.password || "",
      notes: device.notes || "",
      branchId: device.branchId || branch?.id || "",
      matchMethod: device.matchMethod || "NONE",
      phoneSuffixDigits: device.phoneSuffixDigits || DEFAULT_SUFFIX_DIGITS,
      apiUrl: device.apiUrl || "",
      apiUsername: device.apiUsername || "admin",
      apiPassword: device.apiPassword || "",
      locationCode: device.locationCode || "1",
      syncOnCreate: Boolean(device.syncOnCreate),
      syncOnUpdate: Boolean(device.syncOnUpdate),
      syncOnDelete: Boolean(device.syncOnDelete),
    });
  };

  const handleToggleDeviceStatus = async (device) => {
    const newStatus = device.status === "Disabled" ? "Active" : "Disabled";
    try {
      await api.put(`/attendance/devices/${device.id}`, { status: newStatus });
      setDevicesList((prev) =>
        prev.map((d) => (d.id === device.id ? { ...d, status: newStatus } : d))
      );
      setAllDevices((prev) =>
        prev.map((d) => (d.id === device.id ? { ...d, status: newStatus } : d))
      );
      toast.success(`Device "${device.name}" ${newStatus === "Active" ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update device status");
    }
  };

  const handleSaveDevice = async () => {
    if (!deviceForm.name?.trim()) {
      toast.error("Device Name is required");
      return;
    }
    const targetBranchId = deviceForm.branchId || biometricBranch?.id;
    if (!targetBranchId) {
      toast.error("Please select a branch for this device");
      return;
    }
    setDeviceSaving(true);
    try {
      if (editingDevice) {
        await api.put(`/attendance/devices/${editingDevice.id}`, {
          ...deviceForm,
          branchId: targetBranchId,
        });
        toast.success("Device updated successfully");
      } else {
        await api.post("/attendance/devices", {
          ...deviceForm,
          branchId: targetBranchId,
        });
        toast.success("Device registered successfully");
      }
      setShowAddDevice(false);
      setEditingDevice(null);
      if (biometricBranch) {
        fetchBranchDevices(biometricBranch.id);
      }
      fetchAllDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save device");
    } finally {
      setDeviceSaving(false);
    }
  };

  const handleDeleteDevice = async (deviceId) => {
    try {
      await api.delete(`/attendance/devices/${deviceId}`);
      toast.success("Device removed");
      if (biometricBranch) fetchBranchDevices(biometricBranch.id);
      fetchAllDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete device");
    }
  };

  // ── Device Reconciliation & Hardware Expiry Sync Handlers ──
  const fetchReconciliation = async (deviceId) => {
    if (!deviceId) return;
    setReconcileLoading(true);
    try {
      const res = await api.get(`/attendance/devices/${deviceId}/reconciliation`);
      setReconcileData(res.data);
      setReconcilePage(1);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load device reconciliation");
    } finally {
      setReconcileLoading(false);
    }
  };

  const handlePullUsers = async () => {
    if (!reconcileDeviceId) return;
    setReconcilePulling(true);
    try {
      const res = await api.post(`/attendance/devices/${reconcileDeviceId}/pull-users`);
      const notify = Object.keys(res.data.ambiguous || {}).length ? toast.warning : toast.success;
      notify(res.data.message || `Pulled ${res.data.count} enrolled users from device`);
      fetchReconciliation(reconcileDeviceId);
      fetchAllDevices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to pull users from device. Check Web API URL.");
    } finally {
      setReconcilePulling(false);
    }
  };

  const handleSyncAllExpiries = async () => {
    if (!reconcileDeviceId) return;
    setReconcileSyncing(true);
    try {
      const res = await api.post(`/attendance/devices/${reconcileDeviceId}/sync-expiry`, {});
      if (res.data?.failedCount > 0) {
        if (res.data.syncedCount === 0) {
          toast.error(`Sync failed: all ${res.data.failedCount} members failed. Check device API credentials.`);
        } else {
          toast.warning(`Synchronized ${res.data.syncedCount} members (${res.data.failedCount} failed)`);
        }
      } else {
        toast.success(res.data.message || "Synchronized members to device");
      }
      fetchReconciliation(reconcileDeviceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to sync expiries to device");
    } finally {
      setReconcileSyncing(false);
    }
  };

  const handleSyncSingleExpiry = async (empCode) => {
    if (!reconcileDeviceId || !empCode) return;
    setSingleSyncingCode(empCode);
    try {
      const res = await api.post(`/attendance/devices/${reconcileDeviceId}/sync-expiry`, { empCodes: [empCode] });
      const firstResult = res.data?.results?.[0];
      if (res.data?.failedCount > 0 || (firstResult && !firstResult.success)) {
        toast.error(`Sync failed for user code ${empCode}: ${firstResult?.error || "Device API error"}`);
      } else {
        toast.success(`Expiry synced for user code ${empCode}`);
      }
      fetchReconciliation(reconcileDeviceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to sync code ${empCode}`);
    } finally {
      setSingleSyncingCode(null);
    }
  };

  const handleOpenMapModal = (empCode, deviceUserName = "") => {
    setMappingCode(empCode);
    setMappingDeviceUserName(deviceUserName);
    setMappingTargetType("member");
    setMappingTargetId("");
    setMappingModalOpen(true);
  };

  const handleSaveMapping = async () => {
    if (!mappingTargetId) {
      toast.error("Please select a member or staff member to map");
      return;
    }
    setMappingSaving(true);
    try {
      const res = await api.post(`/attendance/devices/${reconcileDeviceId}/map-user`, {
        empCode: mappingCode,
        targetType: mappingTargetType,
        targetId: mappingTargetId,
      });
      toast.success(res.data.message || "User mapped successfully");
      setMappingModalOpen(false);
      fetchReconciliation(reconcileDeviceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to map user to device");
    } finally {
      setMappingSaving(false);
    }
  };

  const blockedReconcileMembers = useMemo(() => {
    if (!reconcileData?.records) return [];
    return reconcileData.records.filter(
      (r) => r.entityType === "member" && (r.isBlocked || r.accessStatus === "Blocked")
    );
  }, [reconcileData]);

  const handleOpenExtendModal = () => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    setExtendDate(d.toISOString().split("T")[0]);
    setExtendScope("all");
    setSelectedExtendMemberIds(blockedReconcileMembers.map((m) => m.entityId).filter(Boolean));
    setExtendReason("Temporary Extension");
    setExtendModalOpen(true);
  };

  const handleSetQuickExtendDate = (daysToAdd) => {
    const d = new Date();
    d.setDate(d.getDate() + daysToAdd);
    setExtendDate(d.toISOString().split("T")[0]);
  };

  const handleSetEndOfMonthDate = () => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setExtendDate(lastDay.toISOString().split("T")[0]);
  };

  const handleSaveExtendAccess = async () => {
    if (!extendDate) {
      toast.error("Please select an extension date");
      return;
    }
    const todayStr = new Date().toISOString().split("T")[0];
    if (extendDate < todayStr) {
      toast.error("Extension date cannot be in the past");
      return;
    }
    const targetIds =
      extendScope === "selected"
        ? selectedExtendMemberIds
        : blockedReconcileMembers.map((m) => m.entityId).filter(Boolean);

    if (targetIds.length === 0) {
      toast.error("No blocked members found on this device to extend");
      return;
    }

    setExtendSubmitting(true);
    try {
      const payload = {
        extendedUntilDate: extendDate,
        memberIds: targetIds,
        reason: extendReason,
      };
      const res = await api.post(`/attendance/devices/${reconcileDeviceId}/extend-blocked-access`, payload);
      toast.success(res.data.message || `Extended turnstile access until ${extendDate}`);
      setExtendModalOpen(false);
      fetchReconciliation(reconcileDeviceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to extend turnstile access");
    } finally {
      setExtendSubmitting(false);
    }
  };

  const handleDeleteDeviceUser = async (empCode) => {
    if (!window.confirm(`Are you sure you want to delete user code "${empCode}" from the physical terminal?`)) return;
    try {
      const res = await api.delete(`/attendance/devices/${reconcileDeviceId}/users/${empCode}`);
      toast.success(res.data.message || "User deleted from device");
      fetchReconciliation(reconcileDeviceId);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user from device");
    }
  };

  const handleTestConnection = async () => {
    if (!deviceForm.apiUrl?.trim()) {
      toast.error("Please enter eBioServer Web API URL first");
      return;
    }
    setConnTesting(true);
    try {
      const res = await api.post("/attendance/devices/test-connection", {
        apiUrl: deviceForm.apiUrl.trim(),
        username: deviceForm.apiUsername || "admin",
        password: deviceForm.apiPassword || "",
        locationCode: deviceForm.locationCode || "1",
      });
      if (res.data.connected) {
        toast.success(res.data.message || "Successfully connected to eBioServer Web API!");
      } else {
        toast.error(res.data.message || "Could not connect to eBioServer Web API");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection test failed");
    } finally {
      setConnTesting(false);
    }
  };

  useEffect(() => {
    if (allDevices.length > 0 && !reconcileDeviceId) {
      const defaultId = getMainDevice(allDevices);
      if (defaultId) setReconcileDeviceId(defaultId);
    }
  }, [allDevices, reconcileDeviceId, getMainDevice]);

  useEffect(() => {
    if (bioActiveSubTab === "reconciliation" && reconcileDeviceId) {
      fetchReconciliation(reconcileDeviceId);
    }
  }, [bioActiveSubTab, reconcileDeviceId]);

  // Filtered devices for Biometric Integration Tab
  const filteredAllDevices = allDevices.filter((d) => {
    if (deviceSearch.trim()) {
      const q = deviceSearch.toLowerCase();
      const matchName = d.name?.toLowerCase().includes(q);
      const matchSerial = d.serialNumber?.toLowerCase().includes(q);
      const matchBranch = d.branchName?.toLowerCase().includes(q);
      const matchProvider = d.provider?.toLowerCase().includes(q);
      if (!matchName && !matchSerial && !matchBranch && !matchProvider) return false;
    }
    if (selectedBranchFilter !== "all" && d.branchId !== selectedBranchFilter) {
      return false;
    }
    if (selectedStatusFilter === "active" && d.status === "Disabled") {
      return false;
    }
    if (selectedStatusFilter === "disabled" && d.status !== "Disabled") {
      return false;
    }
    if (selectedStatusFilter === "online" && !getConnectionStatus(d).isOnline) {
      return false;
    }
    if (selectedStatusFilter === "awaiting" && (d.lastPunchAt || d.status === "Disabled")) {
      return false;
    }
    return true;
  });

  const onlineDevicesCount = allDevices.filter((d) => getConnectionStatus(d).isOnline).length;
  const configuredBranchesCount = new Set(allDevices.map((d) => d.branchId).filter(Boolean)).size;
  const awaitingCount = allDevices.filter((d) => !d.lastPunchAt && d.status !== "Disabled").length;

  const renderDeviceForm = ({ isEditing = false, device = null, onCancel = null } = {}) => (
    <Card className="bg-secondary/15 border border-primary/30 rounded-sm p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="text-xs font-heading font-bold uppercase tracking-wider text-primary">
          {isEditing && (device || editingDevice)
            ? `Edit Device: ${(device || editingDevice).name}`
            : `Register New ${selectedBrand.toUpperCase()} Device`}
        </h5>
        {(device || editingDevice)?.isDefault && (
          <Badge variant="outline" className="text-[10px] font-mono border-primary/40 text-primary">
            Default Device
          </Badge>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs">Device Name *</Label>
          <Input
            value={deviceForm.name}
            onChange={(e) => setDeviceForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="e.g. Primary Biometric Device"
            className="rounded-sm h-8 text-xs mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Serial Number (Optional)</Label>
          <Input
            value={deviceForm.serialNumber}
            onChange={(e) => setDeviceForm((p) => ({ ...p, serialNumber: e.target.value }))}
            placeholder="e.g. AEXY182960104 (Optional)"
            className="rounded-sm h-8 text-xs font-mono mt-1"
          />
        </div>
        <div>
          <Label className="text-xs">Direction Mode</Label>
          <Select
            value={deviceForm.directionMode}
            onValueChange={(v) => setDeviceForm((p) => ({ ...p, directionMode: v }))}
          >
            <SelectTrigger className="rounded-sm h-8 text-xs mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AUTO">Auto (Smart In/Out Toggle)</SelectItem>
              <SelectItem value="IN">Check-In Only (Entry Turnstile)</SelectItem>
              <SelectItem value="OUT">Check-Out Only (Exit Gate)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <MatchMethodFields form={deviceForm} setForm={setDeviceForm} />
        <div className="sm:col-span-2">
          <Label className="text-xs">Encryption Password (Optional)</Label>
          <Input
            value={deviceForm.password}
            onChange={(e) => setDeviceForm((p) => ({ ...p, password: e.target.value }))}
            placeholder="Optional device/webhook password"
            className="rounded-sm h-8 text-xs mt-1"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Must match the Web Hook encryption password in eBioServerNew. When set, only encrypted punches are accepted; leave empty only if encryption is off in eBioServerNew.
          </p>
        </div>

        {/* eBioServer Web API Settings */}
        <div className="sm:col-span-2 pt-3 border-t border-border/40 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs font-semibold flex items-center gap-1.5 text-primary">
                <Globe className="h-3.5 w-3.5" />
                eBioServer Web API (Hardware Expiry & User Sync)
              </Label>
              <p className="text-[11px] text-muted-foreground">
                Enables automated turnstile locking based on membership expiry + grace period and device reconciliation.
              </p>
            </div>
            {deviceForm.apiUrl?.trim() && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs rounded-sm font-heading"
                onClick={handleTestConnection}
                disabled={connTesting}
              >
                <RefreshCw className={`h-3 w-3 mr-1 ${connTesting ? "animate-spin" : ""}`} />
                {connTesting ? "Testing..." : "Test Connection"}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
            <div className="sm:col-span-2">
              <Label className="text-xs">eBioServer Web API Service URL</Label>
              <Input
                value={deviceForm.apiUrl || ""}
                onChange={(e) => setDeviceForm((p) => ({ ...p, apiUrl: e.target.value }))}
                placeholder="http://192.168.1.100:8080/webservice.asmx"
                className="rounded-sm h-8 text-xs mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="text-xs">API Username</Label>
              <Input
                value={deviceForm.apiUsername || ""}
                onChange={(e) => setDeviceForm((p) => ({ ...p, apiUsername: e.target.value }))}
                placeholder="admin"
                className="rounded-sm h-8 text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">API Password</Label>
              <Input
                type="password"
                value={deviceForm.apiPassword || ""}
                onChange={(e) => setDeviceForm((p) => ({ ...p, apiPassword: e.target.value }))}
                placeholder="eBioServer password"
                className="rounded-sm h-8 text-xs mt-1"
              />
            </div>
            <div>
              <Label className="text-xs">Location Code</Label>
              <Input
                value={deviceForm.locationCode || "1"}
                onChange={(e) => setDeviceForm((p) => ({ ...p, locationCode: e.target.value }))}
                placeholder="1"
                className="rounded-sm h-8 text-xs mt-1 font-mono"
              />
            </div>
          </div>
        </div>

        {/* Device-Level Lifecycle Sync Controls (Strict Opt-In) */}
        <div className="sm:col-span-2 pt-3 border-t border-border/40 space-y-2">
          <Label className="text-xs font-semibold flex items-center gap-1.5 text-primary">
            <ShieldCheck className="h-3.5 w-3.5" />
            Automatic Validity Push (Device Protection)
          </Label>
          <p className="text-[11px] text-muted-foreground">
            Strictly opt-in: Choose which membership lifecycle actions automatically push validity to this device.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1">
            <label className="flex items-start gap-2 p-2 rounded-sm border border-border/60 bg-secondary/20 cursor-pointer hover:bg-secondary/40 transition-colors">
              <input
                type="checkbox"
                checked={Boolean(deviceForm.syncOnCreate)}
                onChange={(e) => setDeviceForm((p) => ({ ...p, syncOnCreate: e.target.checked }))}
                className="mt-0.5 rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
              />
              <div className="text-[11px]">
                <span className="font-semibold block text-foreground">Sync on Create / Renew</span>
                <span className="text-muted-foreground text-[10px]">Pushes validity when membership is assigned or renewed.</span>
              </div>
            </label>

            <label className="flex items-start gap-2 p-2 rounded-sm border border-border/60 bg-secondary/20 cursor-pointer hover:bg-secondary/40 transition-colors">
              <input
                type="checkbox"
                checked={Boolean(deviceForm.syncOnUpdate)}
                onChange={(e) => setDeviceForm((p) => ({ ...p, syncOnUpdate: e.target.checked }))}
                className="mt-0.5 rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
              />
              <div className="text-[11px]">
                <span className="font-semibold block text-foreground">Sync on Update</span>
                <span className="text-muted-foreground text-[10px]">Pushes validity when membership dates are edited.</span>
              </div>
            </label>

            <label className="flex items-start gap-2 p-2 rounded-sm border border-border/60 bg-secondary/20 cursor-pointer hover:bg-secondary/40 transition-colors">
              <input
                type="checkbox"
                checked={Boolean(deviceForm.syncOnDelete)}
                onChange={(e) => setDeviceForm((p) => ({ ...p, syncOnDelete: e.target.checked }))}
                className="mt-0.5 rounded border-border text-primary focus:ring-primary h-3.5 w-3.5"
              />
              <div className="text-[11px]">
                <span className="font-semibold block text-foreground">Sync on Delete / Cancel</span>
                <span className="text-muted-foreground text-[10px]">Blocks device access if membership is deleted.</span>
              </div>
            </label>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2 border-t border-border/40">
        <Button
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={onCancel || (() => { setShowAddDevice(false); setEditingDevice(null); })}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          className="h-8 text-xs font-heading uppercase"
          onClick={handleSaveDevice}
          disabled={deviceSaving}
        >
          {deviceSaving ? "Saving..." : isEditing ? "Update Device" : "Save Device"}
        </Button>
      </div>
    </Card>
  );

  // Filtered and paginated records for Device Reconciliation tab
  const filteredReconcileRows = useMemo(() => {
    const source =
      reconcileFilter === "missing"
        ? reconcileData?.missingCrm || []
        : reconcileData?.records || [];

    return source.filter((row) => {
      if (reconcileFilter === "active" && (row.entityType !== "member" || row.isBlocked)) return false;
      if (reconcileFilter === "expired" && (row.entityType !== "member" || !row.isBlocked)) return false;
      if (reconcileFilter === "staff" && row.entityType !== "staff") return false;
      if (reconcileFilter === "unmapped" && row.entityType !== "unmapped") return false;

      if (reconcileSearch.trim()) {
        const q = reconcileSearch.toLowerCase();
        const matchName = String(row.name || "").toLowerCase().includes(q);
        const matchDevName = String(row.deviceUserName || "").toLowerCase().includes(q);
        const matchPhone = String(row.phone || "").toLowerCase().includes(q);
        const matchCode = String(row.deviceCode || "").toLowerCase().includes(q);
        const matchPlan = String(row.planName || "").toLowerCase().includes(q);
        const matchNum = String(row.memberNumber || "").toLowerCase().includes(q);
        if (!matchName && !matchDevName && !matchPhone && !matchCode && !matchPlan && !matchNum) return false;
      }
      return true;
    });
  }, [reconcileData, reconcileFilter, reconcileSearch]);

  const reconcileTotal = filteredReconcileRows.length;
  const reconcileTotalPages = Math.max(1, Math.ceil(reconcileTotal / reconcilePageSize));
  const paginatedReconcileRows = useMemo(() => {
    const start = (reconcilePage - 1) * reconcilePageSize;
    return filteredReconcileRows.slice(start, start + reconcilePageSize);
  }, [filteredReconcileRows, reconcilePage, reconcilePageSize]);

  return (
    <div className="space-y-6" data-testid="branches-page">
      {/* ── Page Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading tracking-tight">Branches & Biometrics</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Manage your physical gym locations, counter check-in QR codes, and hardware biometric turnstiles
          </p>
        </div>

        {mainTab === "branches" ? (
          <Button
            size="sm"
            className="rounded-sm"
            onClick={() => {
              setEditing(null);
              setForm({ name: "", address: "", phone: "", allowPublicSignup: false });
              setShowForm(true);
            }}
            data-testid="add-branch-btn"
          >
            <Plus className="h-4 w-4 mr-1" /> Add Branch
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm text-xs"
              onClick={fetchAllDevices}
              disabled={allDevicesLoading}
            >
              <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${allDevicesLoading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              size="sm"
              className="rounded-sm text-xs font-heading"
              onClick={() => handleOpenAddBiometricGlobal()}
              data-testid="add-biometric-device-btn"
            >
              <Plus className="h-4 w-4 mr-1" /> Add Biometric Device
            </Button>
          </div>
        )}
      </div>

      {/* ── Top-Level Tabs: Branches & Biometric Integration ── */}
      <Tabs value={mainTab} onValueChange={setMainTab} className="w-full space-y-4">
        <TabsList className="grid grid-cols-2 max-w-md h-10 bg-secondary/50 p-1 border border-border rounded-sm">
          <TabsTrigger
            value="branches"
            className="font-heading text-xs uppercase tracking-wide flex items-center justify-center gap-2 rounded-sm"
            data-testid="tab-branches"
          >
            <Building2 className="h-3.5 w-3.5" />
            Branches ({branchList.length})
          </TabsTrigger>
          <TabsTrigger
            value="biometrics"
            className="font-heading text-xs uppercase tracking-wide flex items-center justify-center gap-2 rounded-sm"
            data-testid="tab-biometrics"
          >
            <Fingerprint className="h-3.5 w-3.5 text-primary" />
            Biometric Integration ({allDevices.length})
          </TabsTrigger>
        </TabsList>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ══ TAB 1: BRANCHES VIEW ══ */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="branches" className="space-y-4 mt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loading ? (
              <p className="text-muted-foreground">Loading...</p>
            ) : branchList.length === 0 ? (
              <p className="text-muted-foreground">No branches yet.</p>
            ) : (
              branchList.map((b) => (
                <Card
                  key={b.id}
                  className={`bg-card border-border rounded-sm transition-colors ${
                    b.isDefault ? "border-primary/40 hover:border-primary/70" : "hover:border-primary/50"
                  }`}
                >
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                      <Building2 className="h-4 w-4 text-primary shrink-0" />
                      <span className="flex-1 truncate">{b.name}</span>
                      {b.isDefault && (
                        <Badge
                          variant="outline"
                          className="text-[10px] px-1.5 py-0 border-primary/40 text-primary flex items-center gap-1 shrink-0"
                        >
                          <ShieldCheck className="h-2.5 w-2.5" />
                          Default
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {b.address && <p className="text-sm text-muted-foreground mb-1 whitespace-pre-line">{b.address}</p>}
                    {b.phone && <p className="text-sm text-muted-foreground mb-3">{b.phone}</p>}

                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => handleQR(b, "signup")}
                        data-testid={`signup-qr-${b.id}`}
                      >
                        <UserPlus className="h-3 w-3 mr-1" /> Signup QR
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => handleQR(b, "attendance")}
                        data-testid={`attendance-qr-${b.id}`}
                      >
                        <CalendarCheck className="h-3 w-3 mr-1" /> Attendance QR
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => handleQR(b, "counter")}
                        data-testid={`counter-qr-${b.id}`}
                      >
                        <QrCode className="h-3 w-3 mr-1" /> Counter QR
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm text-primary hover:bg-primary/10 border-primary/30"
                        onClick={() => handleBiometric(b)}
                        data-testid={`biometric-btn-${b.id}`}
                      >
                        <Radio className="h-3 w-3 mr-1" /> Biometric Devices
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="rounded-sm"
                        onClick={() => {
                          setEditing(b);
                          setForm({
                            name: b.name,
                            address: b.address || "",
                            phone: b.phone || "",
                            allowPublicSignup: b.allowPublicSignup || false,
                          });
                          setShowForm(true);
                        }}
                      >
                        <Pencil className="h-3 w-3 mr-1" /> Edit
                      </Button>

                      {!b.isDefault && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="rounded-sm text-destructive hover:bg-destructive/10"
                          onClick={() => setDeleteModal({ open: true, branch: b })}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* ══════════════════════════════════════════════════════════════════ */}
        {/* ══ TAB 2: BIOMETRIC INTEGRATION VIEW ══ */}
        {/* ══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="biometrics" className="space-y-4 mt-0">
          {/* Quick Metrics Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="bg-card border-border rounded-sm p-3.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-heading uppercase text-muted-foreground tracking-wider">Total Devices</p>
                  <p className="text-xl font-bold font-heading mt-0.5">{allDevices.length}</p>
                </div>
                <div className="h-8 w-8 rounded-sm bg-primary/10 flex items-center justify-center text-primary">
                  <Cpu className="h-4 w-4" />
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border rounded-sm p-3.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-heading uppercase text-muted-foreground tracking-wider">Online / Active</p>
                  <p className="text-xl font-bold font-heading text-emerald-400 mt-0.5">{onlineDevicesCount}</p>
                </div>
                <div className="h-8 w-8 rounded-sm bg-emerald-500/10 flex items-center justify-center text-emerald-400">
                  <Wifi className="h-4 w-4" />
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border rounded-sm p-3.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-heading uppercase text-muted-foreground tracking-wider">Branches Covered</p>
                  <p className="text-xl font-bold font-heading mt-0.5">
                    {configuredBranchesCount} / {branchList.length}
                  </p>
                </div>
                <div className="h-8 w-8 rounded-sm bg-primary/10 flex items-center justify-center text-primary">
                  <Building2 className="h-4 w-4" />
                </div>
              </div>
            </Card>

            <Card className="bg-card border-border rounded-sm p-3.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] font-heading uppercase text-muted-foreground tracking-wider">Awaiting Punch</p>
                  <p className="text-xl font-bold font-heading text-sky-400 mt-0.5">{awaitingCount}</p>
                </div>
                <div className="h-8 w-8 rounded-sm bg-sky-500/10 flex items-center justify-center text-sky-400">
                  <Radio className="h-4 w-4" />
                </div>
              </div>
            </Card>
          </div>

          {/* Sub-navigation: Devices, Punch Logs, Raw Webhook Stream, Device Users & Sync */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-border/70 pb-3">
            <div className="flex flex-wrap items-center gap-1.5 p-1 bg-secondary/30 rounded-sm border border-border w-fit">
              <Button
                variant={bioActiveSubTab === "devices" ? "default" : "ghost"}
                size="sm"
                className="text-xs h-7 px-3 rounded-sm font-heading"
                onClick={() => setBioActiveSubTab("devices")}
              >
                <Cpu className="h-3.5 w-3.5 mr-1.5" />
                Devices
              </Button>
              <Button
                variant={bioActiveSubTab === "punches" ? "default" : "ghost"}
                size="sm"
                className="text-xs h-7 px-3 rounded-sm font-heading"
                onClick={() => {
                  setBioActiveSubTab("punches");
                  fetchPunchLogs(punchPage, punchPageSize, false, getActiveBranchId());
                }}
              >
                <CalendarCheck className="h-3.5 w-3.5 mr-1.5" />
                Punch Logs
              </Button>
              <Button
                variant={bioActiveSubTab === "raw" ? "default" : "ghost"}
                size="sm"
                className="text-xs h-7 px-3 rounded-sm font-heading"
                onClick={() => {
                  setBioActiveSubTab("raw");
                  fetchRawLogs(rawPage, rawPageSize, false, getActiveBranchId());
                }}
              >
                <Terminal className="h-3.5 w-3.5 mr-1.5 text-primary" />
                Raw Webhook Stream
              </Button>
              <Button
                variant={bioActiveSubTab === "reconciliation" ? "default" : "ghost"}
                size="sm"
                className="text-xs h-7 px-3 rounded-sm font-heading"
                onClick={() => {
                  setBioActiveSubTab("reconciliation");
                  const devId = reconcileDeviceId || getMainDevice(allDevices);
                  if (devId) fetchReconciliation(devId);
                }}
              >
                <UserCheck className="h-3.5 w-3.5 mr-1.5 text-primary" />
                Device Users & Sync
              </Button>
              <Button
                variant={bioActiveSubTab === "sync-logs" ? "default" : "ghost"}
                size="sm"
                className="text-xs h-7 px-3 rounded-sm font-heading"
                onClick={() => {
                  setBioActiveSubTab("sync-logs");
                  fetchSyncLogs(1, syncLogsPageSize, syncLogsSearch, syncLogsStatusFilter);
                }}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5 text-primary" />
                Device Sync Logs
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Branch / Device Filter: only needed on punches, raw webhooks, and device users sync */}
              {bioActiveSubTab !== "devices" && allDevices.length > 0 && (
                <div className="flex items-center gap-1.5 px-2 py-0.5 bg-secondary/30 border border-border rounded-sm">
                  <Cpu className="h-3.5 w-3.5 text-primary" />
                  <Select
                    value={reconcileDeviceId || getMainDevice(allDevices)}
                    onValueChange={handleDeviceBranchChange}
                  >
                    <SelectTrigger className="h-7 text-xs bg-transparent border-0 min-w-[130px] max-w-[200px] focus:ring-0 shadow-none px-1">
                      <SelectValue placeholder="Select Device / Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      {allDevices.map((d) => (
                        <SelectItem key={d.id} value={d.id} className="text-xs">
                          {d.name} {d.branchName ? `(${d.branchName})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}


              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs rounded-sm self-start sm:self-auto font-heading uppercase"
                onClick={handleRefreshAllBiometrics}
                disabled={allDevicesLoading || globalLogsLoading || reconcileLoading}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${(allDevicesLoading || globalLogsLoading || reconcileLoading) ? "animate-spin" : ""}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* ── SUB-TAB 1: DEVICES VIEW ── */}
          {bioActiveSubTab === "devices" && (
            <div className="space-y-3">
              {/* Search & Filters */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 p-3 bg-card border border-border rounded-sm">
                <div className="flex-1 relative">
                  <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                    placeholder="Search devices by name, serial number, or branch..."
                    className="pl-8 h-9 text-xs rounded-sm bg-background border-border"
                  />
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="font-mono text-xs h-9 px-2.5 flex items-center">
                    Total: {filteredAllDevices.length} {filteredAllDevices.length === 1 ? "device" : "devices"}
                  </Badge>

                  {/* Status Filter */}
                  <Select value={selectedStatusFilter} onValueChange={setSelectedStatusFilter}>
                    <SelectTrigger className="w-36 h-9 text-xs rounded-sm bg-background">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="text-xs">All Status</SelectItem>
                      <SelectItem value="online" className="text-xs">Online / Active</SelectItem>
                      <SelectItem value="awaiting" className="text-xs">Awaiting Punch</SelectItem>
                      <SelectItem value="disabled" className="text-xs">Disabled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Global Devices Table */}
              <div className="border border-border rounded-sm bg-card overflow-hidden">
                {allDevicesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent mr-2" />
                    <span className="text-xs text-muted-foreground">Loading biometric devices...</span>
                  </div>
                ) : filteredAllDevices.length === 0 ? (
                  <div className="text-center py-12 px-4 space-y-3">
                    <Cpu className="h-8 w-8 mx-auto text-muted-foreground/40" />
                    <div>
                      <p className="text-sm font-semibold">No Biometric Devices Found</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {deviceSearch || selectedBranchFilter !== "all" || selectedStatusFilter !== "all"
                          ? "Try changing your search or filter options"
                          : "Register your first biometric device to sync hardware turnstiles and attendance"}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      className="rounded-sm text-xs font-heading uppercase"
                      onClick={() => handleOpenAddBiometricGlobal()}
                    >
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Biometric Device
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20">
                          <TableHead className="text-xs">Device Name</TableHead>
                          <TableHead className="text-xs">Branch</TableHead>
                          <TableHead className="text-xs">Brand / Provider</TableHead>
                          <TableHead className="text-xs">Direction</TableHead>
                          <TableHead className="text-xs">ID Resolution</TableHead>
                          <TableHead className="text-xs">Serial Number</TableHead>
                          <TableHead className="text-xs">Connection Status</TableHead>
                          <TableHead className="text-xs">Active</TableHead>
                          <TableHead className="text-xs text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredAllDevices.map((d) => {
                          const conn = getConnectionStatus(d);
                          const branch = branchList.find((b) => b.id === d.branchId);

                          return (
                            <TableRow key={d.id} className="hover:bg-secondary/15">
                              <TableCell className="font-medium text-xs">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className="font-semibold text-foreground">{d.name}</span>
                                  {d.isDefault && (
                                    <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary/40 text-primary font-mono">
                                      Default
                                    </Badge>
                                  )}
                                </div>
                              </TableCell>

                              <TableCell className="text-xs">
                                <Badge variant="secondary" className="font-normal text-[11px] flex items-center gap-1 w-fit">
                                  <Building2 className="h-3 w-3 text-primary" />
                                  {d.branchName || branch?.name || "Unassigned"}
                                </Badge>
                              </TableCell>

                              <TableCell className="text-xs uppercase font-mono text-muted-foreground">
                                {d.provider || "essl"}
                              </TableCell>

                              <TableCell>
                                <Badge variant="outline" className="text-[10px] font-mono">
                                  {d.directionMode || "AUTO"}
                                </Badge>
                              </TableCell>

                              <TableCell>
                                <div className="flex flex-col gap-1">
                                  <MatchMethodBadge device={d} />
                                  <div className="flex items-center gap-1 flex-wrap">
                                    {d.syncOnCreate && <span className="inline-flex items-center text-[9px] px-1 py-0.2 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 font-mono">Create</span>}
                                    {d.syncOnUpdate && <span className="inline-flex items-center text-[9px] px-1 py-0.2 rounded border border-blue-500/30 bg-blue-500/10 text-blue-600 font-mono">Update</span>}
                                    {d.syncOnDelete && <span className="inline-flex items-center text-[9px] px-1 py-0.2 rounded border border-rose-500/30 bg-rose-500/10 text-rose-600 font-mono">Delete</span>}
                                    {!d.syncOnCreate && !d.syncOnUpdate && !d.syncOnDelete && <span className="text-[9px] text-muted-foreground/60 font-mono italic">No auto-sync</span>}
                                  </div>
                                </div>
                              </TableCell>

                              <TableCell className="font-mono text-xs text-muted-foreground">
                                {d.serialNumber || (
                                  <span className="text-muted-foreground/60 italic text-[11px]">Optional (Uses Webhook)</span>
                                )}
                              </TableCell>

                              <TableCell>
                                <div className="flex flex-col gap-0.5">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`h-2 w-2 rounded-full ${conn.dotClass}`} />
                                    <Badge variant="outline" className={`text-[10px] font-medium border px-1.5 py-0 ${conn.badgeClass}`}>
                                      {conn.label}
                                    </Badge>
                                  </div>
                                  <span className="text-[10px] text-muted-foreground pl-3.5">{conn.timeText}</span>
                                </div>
                              </TableCell>

                              <TableCell>
                                <Switch
                                  checked={d.status !== "Disabled"}
                                  onCheckedChange={() => handleToggleDeviceStatus(d)}
                                  data-testid={`global-toggle-${d.id}`}
                                />
                              </TableCell>

                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-primary hover:bg-primary/10"
                                    onClick={() => {
                                      if (branch) handleBiometric(branch);
                                      else handleBiometric(branchList[0]);
                                    }}
                                    title="Open Branch Webhook & Punch Stream"
                                  >
                                    <Radio className="h-3.5 w-3.5" />
                                  </Button>

                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                    onClick={() => handleOpenEditDevice(d, branch)}
                                    title="Edit Device"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>

                                  {d.isDefault ? (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-muted-foreground/20 cursor-not-allowed"
                                      disabled
                                      title="Default branch device cannot be deleted"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  ) : (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                      onClick={() => handleDeleteDevice(d.id)}
                                      title="Delete Device"
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── SUB-TAB 2: PROCESSED PUNCH LOGS ── */}
          {bioActiveSubTab === "punches" && (
            <div className="border border-border rounded-sm bg-card overflow-hidden">
              <div className="p-3 bg-secondary/20 border-b border-border flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-heading font-semibold uppercase tracking-wider">Processed Punch Stream</h4>
                  <p className="text-[11px] text-muted-foreground">Audit logs of matched member and staff attendance punches</p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-[10px]">
                    Total: {punchTotal.toLocaleString()} events
                  </Badge>
                  <Badge variant="outline" className="text-[10px] text-muted-foreground border-border/60 font-mono inline-flex items-center gap-1 h-6 px-1.5">
                    <Clock className="h-2.5 w-2.5 text-primary" />
                    7-Day Retention
                  </Badge>
                  {punchTotal > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setLogDeleteModal({ open: true, type: "clear_punches", target: null })}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Clear All
                    </Button>
                  )}
                </div>
              </div>

              {globalLogsLoading && globalPunchLogs.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent mr-2" />
                  <span className="text-xs text-muted-foreground">Loading punch logs...</span>
                </div>
              ) : globalPunchLogs.length === 0 ? (
                <div className="text-center py-10 space-y-1">
                  <CalendarCheck className="h-6 w-6 mx-auto text-muted-foreground/40" />
                  <p className="text-xs font-medium">No Processed Punches Yet</p>
                  <p className="text-[11px] text-muted-foreground">Punches recognized from hardware turnstiles will appear here</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20">
                          <TableHead className="text-xs w-[105px]">Log ID</TableHead>
                          <TableHead className="text-xs">Punch Time</TableHead>
                          <TableHead className="text-xs">User Code</TableHead>
                          <TableHead className="text-xs">Type</TableHead>
                          <TableHead className="text-xs">Matched Entity</TableHead>
                          <TableHead className="text-xs">Direction</TableHead>
                          <TableHead className="text-xs">Action Taken</TableHead>
                          <TableHead className="text-xs">Status</TableHead>
                          <TableHead className="text-xs text-right">Delete</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {globalPunchLogs.map((log) => {
                          const logId = log.id || log._id;
                          return (
                            <TableRow key={logId || log.punchTime} className="hover:bg-secondary/15">
                              <TableCell className="font-mono text-[11px]">
                                {logId ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(logId);
                                      toast.success("Log ID copied to clipboard");
                                    }}
                                    className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary px-1.5 py-0.5 rounded transition-colors group cursor-pointer max-w-[95px]"
                                    title={`Click to copy: ${logId}`}
                                  >
                                    <span className="truncate">{logId}</span>
                                    <Copy className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                  </button>
                                ) : (
                                  <span className="text-muted-foreground/50 text-[10px]">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-[11px] font-mono whitespace-nowrap">
                                {log.punchTime ? new Date(log.punchTime).toLocaleString("en-IN") : "—"}
                              </TableCell>
                              <TableCell className="text-xs font-mono font-bold text-foreground">
                                {log.userCode}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  variant={log.userType === "member" ? "default" : log.userType === "staff" ? "secondary" : "outline"}
                                  className="text-[10px] uppercase font-mono"
                                >
                                  {log.userType}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs font-medium">
                                {log.entityName ? (
                                  <div className="flex items-center gap-2">
                                    <ClickableAvatar
                                      src={log.entityPhoto}
                                      name={log.entityName}
                                      className="h-7 w-7 shrink-0"
                                      fallbackClassName="bg-primary/10 text-primary text-[10px] font-bold"
                                    />
                                    <div className="flex flex-col min-w-0">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          if (log.userType === "staff" || log.entityType === "staff") {
                                            navigate("/staff");
                                          } else if (log.entityId) {
                                            navigate(`/members/${log.entityId}`);
                                          }
                                        }}
                                        className="text-xs font-semibold text-foreground hover:text-primary hover:underline truncate max-w-[150px] text-left"
                                        title={`View ${log.userType === "staff" ? "Staff" : "Member"} Profile`}
                                      >
                                        {log.entityName}
                                      </button>
                                      {log.memberNumber && (
                                        <span className="text-[10px] font-mono text-muted-foreground">
                                          #{log.memberNumber}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground italic text-xs">Unmapped</span>
                                )}
                              </TableCell>
                              <TableCell className="text-xs font-mono uppercase">
                                <Badge variant="outline" className="text-[10px]">
                                  {log.direction || "AUTO"}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs capitalize font-medium">
                                {log.action?.replace("_", " ") || "—"}
                              </TableCell>
                              <TableCell>
                                <Badge
                                  className={`text-[10px] ${
                                    log.status === "SUCCESS"
                                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                                      : log.status === "WARNING"
                                      ? "bg-amber-500/15 text-amber-400 border-amber-500/20"
                                      : "bg-red-500/15 text-red-400 border-red-500/20"
                                  }`}
                                >
                                  {log.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                  onClick={() => setLogDeleteModal({ open: true, type: "single_punch", target: log })}
                                  title="Delete punch log"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Punch Logs Pagination Footer */}
                  <div className="p-2.5 bg-secondary/10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                    <div className="text-muted-foreground text-[11px]">
                      Showing{" "}
                      <span className="font-semibold text-foreground">
                        {punchTotal === 0 ? 0 : (punchPage - 1) * punchPageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-semibold text-foreground">
                        {Math.min(punchPage * punchPageSize, punchTotal)}
                      </span>{" "}
                      of{" "}
                      <span className="font-semibold text-foreground">
                        {punchTotal.toLocaleString()}
                      </span>{" "}
                      punches
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground text-[11px]">Rows:</span>
                        <Select
                          value={String(punchPageSize)}
                          onValueChange={handlePunchPageSizeChange}
                        >
                          <SelectTrigger className="h-7 w-[68px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                            <SelectItem value="200">200</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
                        <span>Page {punchPage} of {Math.max(1, Math.ceil(punchTotal / punchPageSize))}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-sm"
                          disabled={punchPage <= 1 || globalLogsLoading}
                          onClick={() => handlePunchPageChange(punchPage - 1)}
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-sm"
                          disabled={punchPage >= Math.max(1, Math.ceil(punchTotal / punchPageSize)) || globalLogsLoading}
                          onClick={() => handlePunchPageChange(punchPage + 1)}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SUB-TAB 3: RAW WEBHOOK STREAM INSPECTOR ── */}
          {bioActiveSubTab === "raw" && (
            <div className="border border-border rounded-sm bg-card overflow-hidden">
              <div className="p-3 bg-secondary/20 border-b border-border flex items-center justify-between">
                <div>
                  <h4 className="text-xs font-heading font-semibold uppercase tracking-wider flex items-center gap-1.5">
                    <Terminal className="h-3.5 w-3.5 text-primary" />
                    Incoming Webhook Ingestion Log (Live Post Data)
                  </h4>
                  <p className="text-[11px] text-muted-foreground">
                    Inspect exact raw HTTP requests sent by eBioServer and biometric machines
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono text-[10px]">
                    Total: {rawTotal.toLocaleString()} payloads
                  </Badge>
                  <Badge variant="outline" className="text-[10px] text-muted-foreground border-border/60 font-mono inline-flex items-center gap-1 h-6 px-1.5">
                    <Clock className="h-2.5 w-2.5 text-primary" />
                    7-Day Retention
                  </Badge>
                  {rawTotal > 0 && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 px-2 text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                      onClick={() => setLogDeleteModal({ open: true, type: "clear_raw", target: null })}
                    >
                      <Trash2 className="h-3 w-3 mr-1" /> Clear All
                    </Button>
                  )}
                </div>
              </div>

              {globalLogsLoading && rawWebhookLogs.length === 0 ? (
                <div className="flex items-center justify-center py-10">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent mr-2" />
                  <span className="text-xs text-muted-foreground">Loading webhook payloads...</span>
                </div>
              ) : rawWebhookLogs.length === 0 ? (
                <div className="text-center py-10 space-y-2">
                  <Terminal className="h-6 w-6 mx-auto text-muted-foreground/40" />
                  <p className="text-xs font-medium">No Webhook Requests Recorded Yet</p>
                  <p className="text-[11px] text-muted-foreground max-w-md mx-auto">
                    When eBioServer hits your branch webhook URL, every request and raw payload will be logged here for inspection.
                  </p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-secondary/20">
                          <TableHead className="text-xs w-[105px]">Log ID</TableHead>
                          <TableHead className="text-xs">Received At</TableHead>
                          <TableHead className="text-xs">Sender IP</TableHead>
                          <TableHead className="text-xs">Provider</TableHead>
                          <TableHead className="text-xs">Punches Extracted</TableHead>
                          <TableHead className="text-xs">Ingestion Status</TableHead>
                          <TableHead className="text-xs">Payload Snippet</TableHead>
                          <TableHead className="text-xs text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rawWebhookLogs.map((item) => {
                          const logId = item.id || item._id;
                          return (
                            <TableRow key={logId || item.createdAt} className="hover:bg-secondary/15">
                              <TableCell className="font-mono text-[11px]">
                                {logId ? (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      navigator.clipboard?.writeText(logId);
                                      toast.success("Log ID copied to clipboard");
                                    }}
                                    className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary px-1.5 py-0.5 rounded transition-colors group cursor-pointer max-w-[95px]"
                                    title={`Click to copy: ${logId}`}
                                  >
                                    <span className="truncate">{logId}</span>
                                    <Copy className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                  </button>
                                ) : (
                                  <span className="text-muted-foreground/50 text-[10px]">—</span>
                                )}
                              </TableCell>
                              <TableCell className="text-[11px] font-mono whitespace-nowrap">
                                {item.createdAt ? new Date(item.createdAt).toLocaleString("en-IN") : "—"}
                              </TableCell>
                              <TableCell className="text-xs font-mono text-muted-foreground">
                                {item.clientIp || "—"}
                              </TableCell>
                              <TableCell className="text-xs font-mono uppercase">
                                {item.provider}
                              </TableCell>
                              <TableCell className="text-xs font-medium">
                                <span className={item.punchesCount > 0 ? "text-emerald-400 font-bold" : "text-amber-400"}>
                                  {item.punchesCount || 0} punch(es)
                                </span>
                              </TableCell>
                              <TableCell>
                                <Badge
                                  className={`text-[10px] ${
                                    item.status === "PROCESSED"
                                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20"
                                      : item.status === "PARSE_ERROR"
                                      ? "bg-red-500/15 text-red-400 border-red-500/20"
                                      : "bg-amber-500/15 text-amber-400 border-amber-500/20"
                                  }`}
                                >
                                  {item.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-[11px] font-mono text-muted-foreground max-w-[200px] truncate">
                                {item.rawBody || "(Empty)"}
                              </TableCell>
                              <TableCell className="text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1.5">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 px-2 text-[11px] font-heading uppercase"
                                    onClick={() => setSelectedRawLog(item)}
                                  >
                                    <Eye className="h-3 w-3 mr-1" /> View
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => setLogDeleteModal({ open: true, type: "single_raw", target: item })}
                                    title="Delete payload"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Raw Logs Pagination Footer */}
                  <div className="p-2.5 bg-secondary/10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                    <div className="text-muted-foreground text-[11px]">
                      Showing{" "}
                      <span className="font-semibold text-foreground">
                        {rawTotal === 0 ? 0 : (rawPage - 1) * rawPageSize + 1}
                      </span>{" "}
                      to{" "}
                      <span className="font-semibold text-foreground">
                        {Math.min(rawPage * rawPageSize, rawTotal)}
                      </span>{" "}
                      of{" "}
                      <span className="font-semibold text-foreground">
                        {rawTotal.toLocaleString()}
                      </span>{" "}
                      payloads
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-muted-foreground text-[11px]">Rows:</span>
                        <Select
                          value={String(rawPageSize)}
                          onValueChange={handleRawPageSizeChange}
                        >
                          <SelectTrigger className="h-7 w-[68px] text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                            <SelectItem value="200">200</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
                        <span>Page {rawPage} of {Math.max(1, Math.ceil(rawTotal / rawPageSize))}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-sm"
                          disabled={rawPage <= 1 || globalLogsLoading}
                          onClick={() => handleRawPageChange(rawPage - 1)}
                        >
                          <ChevronLeft className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-7 w-7 rounded-sm"
                          disabled={rawPage >= Math.max(1, Math.ceil(rawTotal / rawPageSize)) || globalLogsLoading}
                          onClick={() => handleRawPageChange(rawPage + 1)}
                        >
                          <ChevronRight className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── SUB-TAB 4: DEVICE USERS & EXPIRY RECONCILIATION ── */}
          {bioActiveSubTab === "reconciliation" && (
            <div className="space-y-4">
              {/* Device Web API Status Banner */}
              <Card className="bg-card border border-border rounded-sm p-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-base font-semibold font-heading flex items-center gap-2">
                        <Cpu className="h-4 w-4 text-primary" />
                        {reconcileData?.device?.name || "Biometric Terminal"}
                      </h3>
                      {reconcileData?.device?.serialNumber && (
                        <Badge variant="outline" className="font-mono text-[11px]">
                          SN: {reconcileData.device.serialNumber}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[11px]">
                        {reconcileData?.device?.branchName || "All Branches"}
                      </Badge>
                      {reconcileData?.device?.apiUrl ? (
                        <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30 text-[11px] flex items-center gap-1">
                          <Globe className="h-3 w-3" />
                          eBioServer Connected
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[11px] flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Web API Not Configured
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Cross-verify terminal enrollments against CRM members & staff. Set membership expiry + grace period to enforce turnstile access lock at the device level.
                    </p>
                    {reconcileData?.device?.lastPullAt && (
                      <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                        <Clock className="h-3 w-3" />
                        Last pulled from hardware: {new Date(reconcileData.device.lastPullAt).toLocaleString("en-IN")}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 flex-wrap">
                    {reconcileData?.device?.apiUrl && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs rounded-sm font-heading"
                        onClick={() => handleTestConnection(reconcileDeviceId)}
                        disabled={connTesting}
                      >
                        <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${connTesting ? "animate-spin" : ""}`} />
                        {connTesting ? "Testing..." : "Test Connection"}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs rounded-sm font-heading"
                      onClick={() => {
                        const dev = allDevices.find(d => d.id === reconcileDeviceId) || reconcileData?.device;
                        if (dev) handleOpenEditDevice(dev);
                      }}
                    >
                      <Pencil className="h-3.5 w-3.5 mr-1.5" />
                      Configure API
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 text-xs rounded-sm font-heading bg-primary/10 border-primary/40 text-primary hover:bg-primary/20"
                      onClick={handlePullUsers}
                      disabled={reconcilePulling || !reconcileDeviceId}
                    >
                      <CloudDownload className={`h-3.5 w-3.5 mr-1.5 ${reconcilePulling ? "animate-spin" : ""}`} />
                      {reconcilePulling ? "Pulling..." : "Pull All Users"}
                    </Button>
                    <Button
                      size="sm"
                      className="h-8 text-xs rounded-sm font-heading uppercase"
                      onClick={handleSyncAllExpiries}
                      disabled={reconcileSyncing || !reconcileDeviceId}
                    >
                      <UploadCloud className={`h-3.5 w-3.5 mr-1.5 ${reconcileSyncing ? "animate-spin" : ""}`} />
                      {reconcileSyncing ? "Syncing..." : "Sync Expiry Dates"}
                    </Button>
                  </div>
                </div>
              </Card>

              {/* 6 Quick Summary Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Card className="p-3 bg-secondary/10 border-border rounded-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-muted-foreground uppercase font-medium">On Device</p>
                      <p className="text-xl font-bold font-heading">{reconcileData?.summary?.totalOnDevice || 0}</p>
                    </div>
                    <Cpu className="h-5 w-5 text-muted-foreground/60" />
                  </div>
                </Card>

                <Card className="p-3 bg-emerald-500/10 border-emerald-500/20 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-emerald-400 uppercase font-medium">Mapped Active</p>
                      <p className="text-xl font-bold font-heading text-emerald-400">{reconcileData?.summary?.mappedActive || 0}</p>
                    </div>
                    <UserCheck className="h-5 w-5 text-emerald-400/70" />
                  </div>
                </Card>

                <Card className="p-3 bg-rose-500/10 border-rose-500/20 rounded-sm flex flex-col justify-between">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-rose-400 uppercase font-medium">Turnstile Blocked</p>
                      <p className="text-xl font-bold font-heading text-rose-400">{reconcileData?.summary?.mappedExpired || 0}</p>
                    </div>
                    <ShieldAlert className="h-5 w-5 text-rose-400/70" />
                  </div>
                  {(reconcileData?.summary?.mappedExpired || 0) > 0 && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-2 h-6 text-[10px] px-2 font-medium bg-rose-500/20 border-rose-500/30 text-rose-300 hover:bg-rose-500/30 hover:text-rose-100 w-full flex items-center justify-center gap-1 rounded-sm"
                      onClick={handleOpenExtendModal}
                    >
                      <CalendarIcon className="h-3 w-3" />
                      Extend Access
                    </Button>
                  )}
                </Card>

                <Card className="p-3 bg-blue-500/10 border-blue-500/20 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-blue-400 uppercase font-medium">Staff Access</p>
                      <p className="text-xl font-bold font-heading text-blue-400">{reconcileData?.summary?.mappedStaff || 0}</p>
                    </div>
                    <ShieldCheck className="h-5 w-5 text-blue-400/70" />
                  </div>
                </Card>

                <Card className="p-3 bg-amber-500/10 border-amber-500/20 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-amber-400 uppercase font-medium">Unmapped Users</p>
                      <p className="text-xl font-bold font-heading text-amber-400">{reconcileData?.summary?.unmappedOnDevice || 0}</p>
                    </div>
                    <AlertTriangle className="h-5 w-5 text-amber-400/70" />
                  </div>
                </Card>

                <Card className="p-3 bg-purple-500/10 border-purple-500/20 rounded-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[11px] text-purple-400 uppercase font-medium">Not on Device</p>
                      <p className="text-xl font-bold font-heading text-purple-400">{reconcileData?.summary?.crmMissingOnDevice || 0}</p>
                    </div>
                    <Users className="h-5 w-5 text-purple-400/70" />
                  </div>
                </Card>
              </div>

              {/* Search & Filter Pills */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-card border border-border rounded-sm">
                <div className="flex-1 relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search by name, phone, device code, or package..."
                    value={reconcileSearch}
                    onChange={(e) => {
                      setReconcileSearch(e.target.value);
                      setReconcilePage(1);
                    }}
                    className="h-8 pl-8 text-xs rounded-sm bg-background border-border"
                  />
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  {[
                    { id: "all", label: "All Records", count: (reconcileData?.records?.length || 0) },
                    { id: "active", label: "Active", count: reconcileData?.summary?.mappedActive || 0 },
                    { id: "expired", label: "Blocked / Expired", count: reconcileData?.summary?.mappedExpired || 0 },
                    { id: "staff", label: "Staff", count: reconcileData?.summary?.mappedStaff || 0 },
                    { id: "unmapped", label: "Unmapped", count: reconcileData?.summary?.unmappedOnDevice || 0 },
                    { id: "missing", label: "CRM Not on Device", count: reconcileData?.summary?.crmMissingOnDevice || 0 },
                  ].map((p) => (
                    <Button
                      key={p.id}
                      variant={reconcileFilter === p.id ? "default" : "outline"}
                      size="sm"
                      className="h-7 text-xs px-2.5 rounded-sm font-heading"
                      onClick={() => {
                        setReconcileFilter(p.id);
                        setReconcilePage(1);
                      }}
                    >
                      {p.label} ({p.count})
                    </Button>
                  ))}
                </div>
              </div>

              {/* Data Table */}
              {reconcileLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent mr-2" />
                  <span className="text-xs text-muted-foreground">Loading device reconciliation data...</span>
                </div>
              ) : (
                <div className="border border-border rounded-sm overflow-hidden bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-secondary/20 hover:bg-secondary/20">
                        <TableHead className="text-xs">Device Code</TableHead>
                        <TableHead className="text-xs">Device Name</TableHead>
                        <TableHead className="text-xs">CRM Identity</TableHead>
                        <TableHead className="text-xs">Phone / Contact</TableHead>
                        <TableHead className="text-xs">Membership Plan</TableHead>
                        <TableHead className="text-xs">Plan End Date</TableHead>
                        <TableHead className="text-xs">Grace Period</TableHead>
                        <TableHead className="text-xs">Hardware Expiry</TableHead>
                        <TableHead className="text-xs">Turnstile Status</TableHead>
                        <TableHead className="text-xs">Sync State</TableHead>
                        <TableHead className="text-xs text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredReconcileRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11} className="text-center py-10 text-muted-foreground text-xs">
                            <div className="space-y-1">
                              <UserCheck className="h-6 w-6 mx-auto text-muted-foreground/40" />
                              <p className="font-medium">No matching user records found</p>
                              <p className="text-[11px]">Click "Pull All Users" above to retrieve latest enrollments from your eBioServer machine.</p>
                            </div>
                          </TableCell>
                        </TableRow>
                      ) : (
                        paginatedReconcileRows.map((row, idx) => (
                          <TableRow key={row.deviceCode || idx} className="hover:bg-secondary/10">
                            <TableCell className="font-mono text-xs font-semibold">
                              <Badge variant="outline" className="font-mono text-xs bg-secondary/30">
                                {row.deviceCode || "—"}
                              </Badge>
                            </TableCell>

                            <TableCell className="text-xs">
                              {row.deviceUserName ? (
                                <span className="font-semibold text-foreground flex items-center gap-1.5">
                                  <Fingerprint className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                                  <span>{row.deviceUserName}</span>
                                </span>
                              ) : (
                                <span className="text-muted-foreground italic text-[11px]">—</span>
                              )}
                            </TableCell>

                            <TableCell>
                              <div className="flex items-center gap-2">
                                <ClickableAvatar
                                  src={row.photoUrl || row.photo}
                                  name={row.name}
                                  className="h-7 w-7 shrink-0"
                                  fallbackClassName="bg-primary/10 text-primary text-[10px] font-bold"
                                />
                                <div className="flex flex-col min-w-0">
                                  {row.entityId ? (
                                    <button
                                      type="button"
                                      onClick={() => {
                                        if (row.entityType === "staff") {
                                          navigate("/staff");
                                        } else {
                                          navigate(`/members/${row.entityId}`);
                                        }
                                      }}
                                      className="text-xs font-semibold text-foreground hover:text-primary hover:underline truncate max-w-[150px] text-left"
                                      title={`View ${row.entityType === "staff" ? "Staff" : "Member"} Profile`}
                                    >
                                      {row.name}
                                    </button>
                                  ) : row.entityType === "unmapped" ? (
                                    <span className="text-xs text-muted-foreground italic">Not Mapped</span>
                                  ) : (
                                    <p className="text-xs font-semibold">{row.name}</p>
                                  )}
                                  {row.memberNumber ? (
                                    <p className="text-[10px] text-muted-foreground font-mono">#{row.memberNumber}</p>
                                  ) : row.role ? (
                                    <Badge variant="outline" className="text-[9px] h-4 px-1 py-0">{row.role}</Badge>
                                  ) : null}
                                </div>
                              </div>
                            </TableCell>

                            <TableCell className="font-mono text-xs text-muted-foreground">
                              {row.phone || "—"}
                            </TableCell>

                            <TableCell className="text-xs">
                              {row.entityType === "staff" ? (
                                <Badge variant="outline" className="text-[10px] bg-blue-500/10 text-blue-400 border-blue-500/20">
                                  Staff Profile
                                </Badge>
                              ) : row.planName && row.planName !== "None" ? (
                                <span className="font-medium text-foreground">{row.planName}</span>
                              ) : (
                                <span className="text-muted-foreground italic text-[11px]">No active plan</span>
                              )}
                            </TableCell>

                            <TableCell className="text-xs text-muted-foreground">
                              {row.membershipEndDate ? row.membershipEndDate : "—"}
                            </TableCell>

                            <TableCell className="text-xs">
                              {row.entityType === "member" && row.gracePeriodDays ? (
                                <Badge variant="outline" className="text-[10px] font-mono bg-secondary/30">
                                  +{row.gracePeriodDays}d grace
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-[11px]">—</span>
                              )}
                            </TableCell>

                            <TableCell className="text-xs font-mono font-semibold">
                              <HardwareExpiry row={row} />
                            </TableCell>

                            <TableCell>
                              <TurnstileStatus row={row} />
                            </TableCell>

                            <TableCell>
                              {row.syncStatus === "In Sync" ? (
                                <span className="text-emerald-400 text-xs flex items-center gap-1 font-medium">
                                  <Check className="h-3 w-3" /> Synced
                                </span>
                              ) : row.syncStatus === "Needs Sync" ? (
                                <span className="text-amber-400 text-xs flex items-center gap-1 font-medium">
                                  <Clock className="h-3 w-3" /> Needs Sync
                                </span>
                              ) : (
                                <span className="text-muted-foreground text-xs">—</span>
                              )}
                            </TableCell>

                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {row.entityType === "unmapped" ? (
                                  <>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="h-7 text-xs px-2 rounded-sm text-primary border-primary/40 hover:bg-primary/10"
                                      onClick={() => handleOpenMapModal(row.deviceCode, row.deviceUserName)}
                                    >
                                      <LinkIcon className="h-3 w-3 mr-1" /> Map
                                    </Button>
                                    {/* <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive rounded-sm"
                                      title="Delete user from device"
                                      onClick={() => handleDeleteDeviceUser(row.deviceCode)}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button> */}
                                  </>
                                ) : row.notOnDevice ? (
                                  // <Button
                                  //   size="sm"
                                  //   variant="outline"
                                  //   className="h-7 text-xs px-2 rounded-sm"
                                  //   onClick={() => handleSyncSingleExpiry(row.deviceCode)}
                                  // >
                                  //   <Plus className="h-3 w-3 mr-1" /> Push to Device
                                  // </Button>
                                  <></>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={singleSyncingCode === row.deviceCode || reconcileSyncing}
                                    className="h-7 text-xs px-2 rounded-sm text-muted-foreground hover:text-foreground"
                                    onClick={() => handleSyncSingleExpiry(row.deviceCode)}
                                  >
                                    {singleSyncingCode === row.deviceCode ? (
                                      <Loader2 className="h-3 w-3 mr-1 animate-spin text-primary" />
                                    ) : (
                                      <UploadCloud className="h-3 w-3 mr-1 text-primary" />
                                    )}
                                    {singleSyncingCode === row.deviceCode ? "Syncing…" : "Sync"}
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>

                  {/* Pagination Footer */}
                  {reconcileTotal > 0 && (
                    <div className="p-3 bg-secondary/10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                      <div className="text-muted-foreground text-[11px]">
                        Showing{" "}
                        <span className="font-semibold text-foreground">
                          {(reconcilePage - 1) * reconcilePageSize + 1}
                        </span>{" "}
                        to{" "}
                        <span className="font-semibold text-foreground">
                          {Math.min(reconcilePage * reconcilePageSize, reconcileTotal)}
                        </span>{" "}
                        of{" "}
                        <span className="font-semibold text-foreground">
                          {reconcileTotal.toLocaleString()}
                        </span>{" "}
                        records
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <span className="text-muted-foreground text-[11px]">Rows:</span>
                          <Select
                            value={String(reconcilePageSize)}
                            onValueChange={(val) => {
                              const newSize = parseInt(val, 10);
                              setReconcilePageSize(newSize);
                              setReconcilePage(1);
                            }}
                          >
                            <SelectTrigger className="h-7 w-[68px] text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="25">25</SelectItem>
                              <SelectItem value="50">50</SelectItem>
                              <SelectItem value="100">100</SelectItem>
                              <SelectItem value="200">200</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
                          <span>
                            Page {reconcilePage} of {reconcileTotalPages}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 rounded-sm"
                            disabled={reconcilePage <= 1}
                            onClick={() => setReconcilePage((p) => Math.max(1, p - 1))}
                          >
                            <ChevronLeft className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-7 w-7 rounded-sm"
                            disabled={reconcilePage >= reconcileTotalPages}
                            onClick={() => setReconcilePage((p) => Math.min(reconcileTotalPages, p + 1))}
                          >
                            <ChevronRight className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── SUB-TAB 5: DEVICE OUTGOING SYNC LOGS ── */}
          {bioActiveSubTab === "sync-logs" && (
            <div className="border border-border rounded-sm bg-card overflow-hidden space-y-0">
              <div className="p-3 bg-secondary/20 border-b border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <h4 className="text-xs font-heading font-semibold uppercase tracking-wider flex items-center gap-1.5">
                    <RefreshCw className="h-3.5 w-3.5 text-primary" />
                    Device Outgoing Sync Logs (Push Validity Stream)
                  </h4>
                  <p className="text-[11px] text-muted-foreground">
                    Audit trail of member validity dates pushed to physical biometric readers with target device API URL.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className="font-mono text-[10px]">
                    Total: {syncLogsTotal.toLocaleString()} logs
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs rounded-sm font-heading"
                    onClick={() => fetchSyncLogs(syncLogsPage, syncLogsPageSize, syncLogsSearch, syncLogsStatusFilter)}
                    disabled={syncLogsLoading}
                  >
                    <RefreshCw className={`h-3 w-3 mr-1 ${syncLogsLoading ? "animate-spin" : ""}`} />
                    Refresh Logs
                  </Button>
                </div>
              </div>

              {/* Filter bar */}
              <div className="p-3 border-b border-border bg-secondary/10 flex flex-col sm:flex-row gap-2 items-center justify-between">
                <div className="relative w-full sm:w-72">
                  <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Search member, empCode, device..."
                    value={syncLogsSearch}
                    onChange={(e) => {
                      setSyncLogsSearch(e.target.value);
                      fetchSyncLogs(1, syncLogsPageSize, e.target.value, syncLogsStatusFilter, true);
                    }}
                    className="h-8 text-xs pl-8 rounded-sm bg-background"
                  />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Select
                    value={syncLogsStatusFilter || "ALL"}
                    onValueChange={(val) => {
                      const v = val === "ALL" ? "" : val;
                      setSyncLogsStatusFilter(v);
                      fetchSyncLogs(1, syncLogsPageSize, syncLogsSearch, v);
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs w-[130px] rounded-sm">
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All Status</SelectItem>
                      <SelectItem value="success">Success</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-secondary/40 text-[11px] uppercase font-mono">
                      <TableHead className="w-[105px]">Log ID</TableHead>
                      <TableHead className="w-[160px]">Timestamp</TableHead>
                      <TableHead>Target Device & API URL</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Member Details</TableHead>
                      <TableHead>Expiry Pushed</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Diagnostics / Response</TableHead>
                      <TableHead className="w-[80px] text-right">Debug</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncLogsLoading && syncLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-28 text-center text-xs text-muted-foreground">
                          <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2 text-primary" />
                          Loading sync logs...
                        </TableCell>
                      </TableRow>
                    ) : syncLogs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="h-28 text-center text-xs text-muted-foreground">
                          No biometric sync logs recorded yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      syncLogs.map((log) => {
                        const isSuccess = log.success === true;
                        const logId = log.id || log._id;
                        const actionLabel =
                          log.action === "membership_create"
                            ? "Create / Renew"
                            : log.action === "membership_update"
                            ? "Update"
                            : log.action === "membership_delete"
                            ? "Delete / Cancel"
                            : log.action === "manual_sync"
                            ? "Manual Sync"
                            : log.action || "Sync";

                        return (
                          <TableRow key={logId || log.timestamp} className="text-xs hover:bg-secondary/20">
                            <TableCell className="font-mono text-[11px]">
                              {logId ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    navigator.clipboard?.writeText(logId);
                                    toast.success("Log ID copied to clipboard");
                                  }}
                                  className="inline-flex items-center gap-1 font-mono text-[10px] text-muted-foreground hover:text-foreground bg-secondary/50 hover:bg-secondary px-1.5 py-0.5 rounded transition-colors group cursor-pointer max-w-[95px]"
                                  title={`Click to copy: ${logId}`}
                                >
                                  <span className="truncate">{logId}</span>
                                  <Copy className="h-2.5 w-2.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                                </button>
                              ) : (
                                <span className="text-muted-foreground/50 text-[10px]">—</span>
                              )}
                            </TableCell>

                            <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                              {log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}
                            </TableCell>

                            <TableCell>
                              <div className="flex flex-col gap-0.5">
                                <span className="font-semibold text-foreground flex items-center gap-1">
                                  <Cpu className="h-3 w-3 text-primary shrink-0" />
                                  {log.deviceName || log.deviceId || "Device"}
                                </span>
                                {log.apiUrl ? (
                                  <span className="font-mono text-[10px] text-muted-foreground bg-secondary/50 px-1 py-0.5 rounded w-fit max-w-[240px] truncate" title={log.apiUrl}>
                                    {log.apiUrl}
                                  </span>
                                ) : (
                                  <span className="text-[10px] text-muted-foreground/60 italic">No API URL</span>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <Badge
                                variant="outline"
                                className={`text-[10px] font-mono uppercase ${
                                  log.action === "membership_delete"
                                    ? "bg-rose-500/10 text-rose-500 border-rose-500/30"
                                    : log.action === "manual_sync"
                                    ? "bg-purple-500/10 text-purple-500 border-purple-500/30"
                                    : "bg-blue-500/10 text-blue-500 border-blue-500/30"
                                }`}
                              >
                                {actionLabel}
                              </Badge>
                            </TableCell>

                            <TableCell>
                              <div className="flex items-center gap-2">
                                <ClickableAvatar
                                  src={log.memberPhoto}
                                  name={log.memberName}
                                  className="h-7 w-7 shrink-0"
                                  fallbackClassName="bg-primary/10 text-primary text-[10px] font-bold"
                                />
                                <div className="flex flex-col min-w-0">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (log.memberId) {
                                        navigate(`/members/${log.memberId}`);
                                      }
                                    }}
                                    className="text-left font-semibold text-foreground hover:text-primary transition-colors truncate text-xs"
                                  >
                                    {log.memberName || "—"}
                                  </button>
                                  <span className="font-mono text-[10px] text-muted-foreground">
                                    Code: {log.empCode || "—"}
                                  </span>
                                </div>
                              </div>
                            </TableCell>

                            <TableCell className="font-mono text-xs">
                              {log.expiryTo === "2000-01-01" ? (
                                <Badge variant="outline" className="text-[10px] bg-rose-500/10 text-rose-500 border-rose-500/30">
                                  Blocked (2000-01-01)
                                </Badge>
                              ) : log.expiryTo === "2099-12-31" ? (
                                <span className="text-blue-400">Permanent (2099)</span>
                              ) : (
                                log.expiryTo || "—"
                              )}
                            </TableCell>

                            <TableCell>
                              {isSuccess ? (
                                <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30 text-[10px] font-normal flex items-center gap-1 w-fit">
                                  <Check className="h-3 w-3" /> Success
                                </Badge>
                              ) : (
                                <Badge className="bg-rose-500/15 text-rose-500 border-rose-500/30 text-[10px] font-normal flex items-center gap-1 w-fit">
                                  <AlertTriangle className="h-3 w-3" /> Failed
                                </Badge>
                              )}
                            </TableCell>

                            <TableCell className="text-[11px] text-muted-foreground max-w-[200px] truncate" title={log.responseMessage}>
                              {log.responseMessage || "—"}
                            </TableCell>

                            <TableCell className="text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 text-[10px] px-2 gap-1 font-mono hover:bg-primary/10 hover:text-primary border-border"
                                onClick={() => setDebugSyncLog(log)}
                                title="Inspect raw request & response"
                              >
                                <Terminal className="h-3 w-3" />
                                <span>Debug</span>
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination footer */}
              {syncLogsTotal > 0 && (
                <div className="p-3 bg-secondary/10 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                  <div className="text-muted-foreground text-[11px]">
                    Showing{" "}
                    <span className="font-semibold text-foreground">
                      {(syncLogsPage - 1) * syncLogsPageSize + 1}
                    </span>{" "}
                    to{" "}
                    <span className="font-semibold text-foreground">
                      {Math.min(syncLogsPage * syncLogsPageSize, syncLogsTotal)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-foreground">
                      {syncLogsTotal.toLocaleString()}
                    </span>{" "}
                    logs
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground text-[11px]">Rows:</span>
                      <Select
                        value={String(syncLogsPageSize)}
                        onValueChange={(val) => {
                          const newSize = parseInt(val, 10);
                          setSyncLogsPageSize(newSize);
                          setSyncLogsPage(1);
                          fetchSyncLogs(1, newSize, syncLogsSearch, syncLogsStatusFilter);
                        }}
                      >
                        <SelectTrigger className="h-7 w-[68px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground">
                      <span>
                        Page {syncLogsPage} of {Math.ceil(syncLogsTotal / syncLogsPageSize) || 1}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-sm"
                        disabled={syncLogsPage <= 1}
                        onClick={() => {
                          const prev = Math.max(1, syncLogsPage - 1);
                          setSyncLogsPage(prev);
                          fetchSyncLogs(prev, syncLogsPageSize, syncLogsSearch, syncLogsStatusFilter);
                        }}
                      >
                        <ChevronLeft className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 rounded-sm"
                        disabled={syncLogsPage >= Math.ceil(syncLogsTotal / syncLogsPageSize)}
                        onClick={() => {
                          const next = syncLogsPage + 1;
                          setSyncLogsPage(next);
                          fetchSyncLogs(next, syncLogsPageSize, syncLogsSearch, syncLogsStatusFilter);
                        }}
                      >
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </TabsContent>
      </Tabs>

      {/* ── Branch Create / Edit Dialog ── */}
      <Dialog open={showForm} onOpenChange={(o) => { if (!saving) setShowForm(o); }}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Create"} Branch</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                className="rounded-sm mt-1"
                data-testid="branch-name"
              />
            </div>
            <div>
              <Label>Address</Label>
              <Textarea
                value={form.address}
                onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
                className="rounded-sm mt-1 resize-none"
                rows={3}
                placeholder="Street, City, State..."
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                className="rounded-sm mt-1"
              />
            </div>
            <div className="flex items-center justify-between py-2 border-t border-b border-border/50">
              <div className="space-y-0.5">
                <Label className="text-sm font-medium">Allow Public Registration</Label>
                <p className="text-xs text-muted-foreground">Allow new members to register publicly for this branch</p>
              </div>
              <Switch
                checked={form.allowPublicSignup || false}
                onCheckedChange={(checked) => setForm((p) => ({ ...p, allowPublicSignup: checked }))}
                data-testid="branch-public-signup"
              />
            </div>
            <Button
              className="w-full rounded-sm"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-branch-btn"
            >
              {saving ? "Saving..." : (editing ? "Update" : "Create") + " Branch"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── QR Dialog ── */}
      <Dialog open={!!qrData} onOpenChange={() => { setQrData(null); setQrBranch(null); }}>
        <DialogContent className="bg-card border-border rounded-sm text-center max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {qrBranch?.name} — {qrData?.type === "signup" ? "Member Signup" : qrData?.type === "counter" ? "Counter Check-In" : "Attendance"} QR
            </DialogTitle>
          </DialogHeader>
          {qrData && (
            <div className="space-y-4">
              <div className="bg-white p-4 rounded-lg inline-block mx-auto">
                <img src={qrData.qrCode} alt="QR Code" className="w-56 h-56" />
              </div>
              <p className="text-xs text-muted-foreground break-all px-4">{resolveFrontendUrl(qrData.url)}</p>
              <p className="text-sm">
                {qrData.type === "signup"
                  ? "New members can scan this QR to fill out the registration form"
                  : qrData.type === "counter"
                  ? "Display this QR code at the gym counter. Members can scan it with their phone camera to check in/out."
                  : "Members scan this QR to check in/out"}
              </p>
              <div className="flex gap-2 justify-center">
                <Button variant="outline" size="sm" className="rounded-sm" onClick={copyUrl}>
                  <Copy className="h-4 w-4 mr-1" /> Copy URL
                </Button>
                <Button size="sm" className="rounded-sm" onClick={downloadQR}>
                  <Download className="h-4 w-4 mr-1" /> Download QR
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Delete Confirm Modal ── */}
      <DeleteConfirmModal
        open={deleteModal.open}
        onOpenChange={(o) => setDeleteModal({ open: o, branch: o ? deleteModal.branch : null })}
        title="Delete Branch"
        description="This branch will be soft-deleted and removed from the list. Members and data associated with this branch are not affected and will remain intact."
        itemName={deleteModal.branch?.name}
        onConfirm={handleDelete}
        loading={deleteLoading}
      />

      {/* ── Biometric Log Delete Confirmation Modal ── */}
      <DeleteConfirmModal
        open={logDeleteModal.open}
        onOpenChange={(o) => {
          if (!o && !logDeleteLoading) {
            setLogDeleteModal({ open: false, type: "", target: null });
          }
        }}
        title={
          logDeleteModal.type === "clear_raw"
            ? "Clear All Webhook Payloads"
            : logDeleteModal.type === "clear_punches"
            ? "Clear All Processed Punches"
            : logDeleteModal.type === "single_raw"
            ? "Delete Webhook Payload"
            : "Delete Punch Log"
        }
        description={
          logDeleteModal.type === "clear_raw"
            ? "Are you sure you want to delete all stored incoming webhook payloads? This will remove all raw HTTP batch history."
            : logDeleteModal.type === "clear_punches"
            ? "Are you sure you want to delete all processed attendance punch audit logs? This action cannot be undone."
            : logDeleteModal.type === "single_raw"
            ? `Delete incoming payload from ${logDeleteModal.target?.clientIp || "device"} received on ${logDeleteModal.target?.createdAt ? new Date(logDeleteModal.target.createdAt).toLocaleString("en-IN") : ""}?`
            : `Delete punch log for user code ${logDeleteModal.target?.userCode} at ${logDeleteModal.target?.punchTime ? new Date(logDeleteModal.target.punchTime).toLocaleString("en-IN") : ""}?`
        }
        itemName={
          logDeleteModal.type === "clear_raw"
            ? `${rawTotal.toLocaleString()} Raw Payloads`
            : logDeleteModal.type === "clear_punches"
            ? `${punchTotal.toLocaleString()} Punch Records`
            : undefined
        }
        onConfirm={handleDeleteLogsConfirm}
        loading={logDeleteLoading}
      />

      {/* ── Reused Biometric Integration Dialog (with Branch Selection) ── */}
      <Dialog
        open={!!biometricBranch}
        onOpenChange={(open) => {
          if (!open) {
            setBiometricBranch(null);
            setShowAddDevice(false);
            setEditingDevice(null);
          }
        }}
      >
        <DialogContent className="bg-card border-border rounded-sm max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 sm:p-6 pb-3 border-b border-border/50">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <DialogTitle className="font-heading text-lg flex items-center gap-2">
                  <Fingerprint className="h-5 w-5 text-primary" />
                  Biometric Integration
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Configure biometric webhook, device turnstiles, and attendance tracking
                </p>
              </div>

              {/* Branch Selection Dropdown */}
              <div className="flex items-center gap-2">
                <Label className="text-xs text-muted-foreground whitespace-nowrap">Branch:</Label>
                <Select
                  value={biometricBranch?.id || ""}
                  onValueChange={(bId) => {
                    const found = branchList.find((b) => b.id === bId);
                    if (found) handleSwitchBiometricBranch(found);
                  }}
                >
                  <SelectTrigger className="h-8 min-w-[190px] text-xs font-semibold bg-background border-primary/30">
                    <Building2 className="h-3.5 w-3.5 mr-1.5 text-primary shrink-0" />
                    <SelectValue placeholder="Select Branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branchList.map((b) => (
                      <SelectItem key={b.id} value={b.id} className="text-xs">
                        {b.name} {b.isDefault ? "(Default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Badge variant="outline" className="font-mono text-[11px] hidden md:inline-flex border-primary/30 text-primary">
                  ID: {biometricBranch?.id?.slice(0, 8)}...
                </Badge>
              </div>
            </div>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
            {/* ══ UNIFIED PROVIDER-DRIVEN SETUP ══ */}
                {/* 1. Provider Selection */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-secondary/30 rounded-sm border border-border">
                  <div className="space-y-0.5">
                    <Label className="text-xs font-semibold uppercase tracking-wide flex items-center gap-1.5">
                      <Radio className="h-3.5 w-3.5 text-primary" /> Device Brand / Provider
                    </Label>
                    <p className="text-xs text-muted-foreground">Select hardware provider to configure webhook URL, devices, and setup guide</p>
                  </div>
                  <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                    <SelectTrigger className="w-60 h-9 rounded-sm bg-background font-medium">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="essl">eSSL / ZKTeco (eBioServer)</SelectItem>
                      <SelectItem value="generic">Generic REST Webhook</SelectItem>
                      <SelectItem value="hikvision" disabled>Hikvision ISAPI (Coming Soon)</SelectItem>
                      <SelectItem value="dahua" disabled>Dahua (Coming Soon)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* 2. Webhook URL Card for Selected Provider */}
                <Card className="bg-primary/5 border-primary/20 rounded-sm">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-heading font-bold uppercase tracking-wider text-primary flex items-center gap-1.5">
                        <Radio className="h-3.5 w-3.5" /> Branch Webhook URL ({selectedBrand.toUpperCase()})
                      </span>
                      <Badge className="bg-primary/20 text-primary border-0 text-[10px] font-mono">
                        Direct Branch Link
                      </Badge>
                    </div>

                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-background border border-border p-2.5 rounded-sm font-mono text-xs text-foreground break-all select-all">
                        {resolveBackendUrl(`/api/attendance/webhook/${selectedBrand}/${orgId}/${biometricBranch?.id}`)}
                      </div>
                      <Button
                        size="sm"
                        className="h-10 px-3 rounded-sm shrink-0 font-heading uppercase text-xs"
                        onClick={copyWebhookUrl}
                      >
                        {copiedWebhook ? <Check className="h-4 w-4 mr-1 text-emerald-400" /> : <Copy className="h-4 w-4 mr-1" />}
                        {copiedWebhook ? "Copied" : "Copy"}
                      </Button>
                    </div>

                    <p className="text-xs text-muted-foreground leading-relaxed">
                      💡 <strong>No Serial Number Required:</strong> When punches hit this URL, they automatically link to <strong>{biometricBranch?.name}</strong>. Hardware serial number registration is completely optional!
                    </p>
                  </CardContent>
                </Card>

                {/* 3. Added Devices Section */}
                <div className="space-y-3 border border-border rounded-sm p-4 bg-card">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-sm font-heading font-semibold flex items-center gap-2">
                        <Cpu className="h-4 w-4 text-primary" />
                        Added Devices ({devicesList.length})
                      </h4>
                      <p className="text-xs text-muted-foreground">Manage turnstiles, readers, and direction modes for {biometricBranch?.name}</p>
                    </div>
                    <Button size="sm" className="rounded-sm text-xs font-heading uppercase" onClick={handleOpenAddDevice}>
                      <Plus className="h-3.5 w-3.5 mr-1" /> Add Device
                    </Button>
                  </div>

                  {/* Add Device Form (Shown only when + Add Device is clicked at top) */}
                  {showAddDevice && !editingDevice && renderDeviceForm({
                    isEditing: false,
                    device: null,
                    onCancel: () => setShowAddDevice(false),
                  })}

                  {/* Devices Table for Selected Branch */}
                  {devicesLoading ? (
                    <div className="flex items-center justify-center py-6">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent mr-2" />
                      <span className="text-xs text-muted-foreground">Loading devices...</span>
                    </div>
                  ) : devicesList.length === 0 ? (
                    <div className="text-center py-6 border border-dashed border-border rounded-sm space-y-1">
                      <Cpu className="h-6 w-6 mx-auto text-muted-foreground/50" />
                      <p className="text-xs font-medium">No Devices Found</p>
                    </div>
                  ) : (
                    <div className="border border-border rounded-sm overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-secondary/20">
                            <TableHead className="text-xs">Device Name</TableHead>
                            <TableHead className="text-xs">Serial Number</TableHead>
                            <TableHead className="text-xs">Brand</TableHead>
                            <TableHead className="text-xs">Direction</TableHead>
                            <TableHead className="text-xs">ID Resolution</TableHead>
                            <TableHead className="text-xs">Connection</TableHead>
                            <TableHead className="text-xs">Status</TableHead>
                            <TableHead className="text-xs text-right">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {devicesList.map((d) => {
                            const conn = getConnectionStatus(d);
                            const isRowEditing = editingDevice?.id === d.id;

                            return (
                              <Fragment key={d.id}>
                                <TableRow className={`transition-colors ${isRowEditing ? "bg-primary/10 border-b-0" : "hover:bg-secondary/10"}`}>
                                  <TableCell className="font-medium text-xs">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                      <span>{d.name}</span>
                                      {d.isDefault && (
                                        <Badge variant="outline" className="text-[9px] px-1 py-0 border-primary/40 text-primary font-mono">
                                          Default
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-mono text-xs text-muted-foreground">
                                    {d.serialNumber || (
                                      <span className="text-muted-foreground/60 italic text-[11px]">Optional (Uses Webhook URL)</span>
                                    )}
                                  </TableCell>
                                  <TableCell className="text-xs uppercase font-mono">{d.provider || "essl"}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="text-[10px] font-mono">
                                      {d.directionMode || "AUTO"}
                                    </Badge>
                                  </TableCell>
                                  <TableCell>
                                    <MatchMethodBadge device={d} />
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-1.5">
                                      <span className={`h-2 w-2 rounded-full ${conn.dotClass}`} />
                                      <span className={`text-[11px] font-medium ${conn.badgeClass.includes("emerald") ? "text-emerald-400" : "text-muted-foreground"}`}>
                                        {conn.label}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex items-center gap-2">
                                      <Switch
                                        checked={d.status !== "Disabled"}
                                        onCheckedChange={() => handleToggleDeviceStatus(d)}
                                        data-testid={`toggle-device-${d.id}`}
                                      />
                                      <span className={`text-[11px] font-mono font-medium ${d.status === "Disabled" ? "text-muted-foreground" : "text-emerald-500"}`}>
                                        {d.status === "Disabled" ? "Disabled" : "Active"}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex items-center justify-end gap-1">
                                      <Button
                                        variant={isRowEditing ? "secondary" : "ghost"}
                                        size="icon"
                                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                        onClick={() => handleOpenEditDevice(d)}
                                        title={isRowEditing ? "Close Editor" : "Edit Device Settings"}
                                      >
                                        {isRowEditing ? (
                                          <ChevronUp className="h-3.5 w-3.5 text-primary" />
                                        ) : (
                                          <Pencil className="h-3.5 w-3.5" />
                                        )}
                                      </Button>
                                      {d.isDefault ? (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 text-muted-foreground/20 cursor-not-allowed"
                                          disabled
                                          title="Default device cannot be deleted"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      ) : (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-7 w-7 text-destructive hover:bg-destructive/10"
                                          onClick={() => handleDeleteDevice(d.id)}
                                          title="Delete Device"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </Button>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>

                                {/* Inline Accordion for Editing Device */}
                                {isRowEditing && (
                                  <TableRow className="bg-secondary/15 hover:bg-secondary/15 border-t-0 border-b-2 border-primary/40">
                                    <TableCell colSpan={8} className="p-3">
                                      {renderDeviceForm({
                                        isEditing: true,
                                        device: d,
                                        onCancel: () => setEditingDevice(null),
                                      })}
                                    </TableCell>
                                  </TableRow>
                                )}
                              </Fragment>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>

                {/* 4. Setup Tips & Checklist for Selected Provider */}
                {selectedBrand === "essl" && (
                  <div className="space-y-3 border border-border rounded-sm p-4 bg-card">
                    <h4 className="text-xs font-heading font-bold uppercase tracking-wider flex items-center gap-1.5 text-primary">
                      <HelpCircle className="h-4 w-4" />
                      eBioServerNew Webhook Setup Guide (5 Steps)
                    </h4>
                    <ol className="space-y-2 text-xs text-muted-foreground list-decimal list-inside leading-relaxed">
                      <li>Open <strong>eBioServerNew</strong> application on your computer or local server.</li>
                      <li>Navigate to <strong>Utilities</strong> &rarr; <strong>Web Hook</strong> in the top menu.</li>
                      <li>Paste the copied <strong>Branch Webhook URL</strong> into the <strong>Web Hook URL</strong> input field.</li>
                      <li>Set <strong>Set Web Hook Response</strong> to: <span className="font-mono font-bold text-foreground bg-secondary px-1.5 py-0.5 rounded">success</span> (all lowercase)</li>
                      <li>Set <strong>Content-Type</strong>: <span className="font-mono text-foreground">Application/Json</span>.</li>
                      <li>Encryption: if this device has an <strong>Encryption Password</strong>, enable encryption in eBioServerNew with the <strong>same password</strong> (punches that are not encrypted with it are rejected). If the device has no Encryption Password, keep encryption off.</li>
                      <li>Click <strong>Save</strong>. Incoming punches will immediately sync to {biometricBranch?.name}!</li>
                    </ol>
                  </div>
                )}

                {selectedBrand === "generic" && (
                  <div className="space-y-3 border border-border rounded-sm p-4 bg-card">
                    <h4 className="text-xs font-heading font-bold uppercase tracking-wider flex items-center gap-1.5 text-primary">
                      <HelpCircle className="h-4 w-4" />
                      Generic REST Webhook Payload Guide
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Configure your biometric hardware or local forwarder script to send HTTP POST requests with JSON payload:
                    </p>
                    <pre className="bg-secondary/40 p-3 rounded text-[11px] font-mono text-foreground overflow-x-auto">
{`{
  "EmployeeCode": "M101",          // Member or Staff biometric ID
  "LogDate": "2026-09-20 10:00:00", // YYYY-MM-DD HH:MM:SS
  "Direction": "IN",               // "IN", "OUT", or leave empty for AUTO
  "SerialNumber": "optional_sn"    // Optional device serial
}`}
                    </pre>
                    <p className="text-xs text-muted-foreground">
                      Server automatically acknowledges with HTTP 200 plain text <span className="font-mono font-bold text-foreground bg-secondary px-1.5 py-0.5 rounded">success</span>.
                    </p>
                  </div>
                )}

                {(selectedBrand === "hikvision" || selectedBrand === "dahua") && (
                  <div className="space-y-2 border border-border rounded-sm p-4 bg-card text-muted-foreground text-xs">
                    <h4 className="text-xs font-heading font-bold uppercase tracking-wider flex items-center gap-1.5 text-primary">
                      <HelpCircle className="h-4 w-4" />
                      {selectedBrand === "hikvision" ? "Hikvision ISAPI" : "Dahua"} Integration Preview
                    </h4>
                    <p>Direct ISAPI event listener integration is coming soon. In the meantime, use the Generic REST Webhook URL with your bridge application.</p>
                  </div>
                )}

          </div>
        </DialogContent>
      </Dialog>

      {/* ── Raw Webhook Payload Inspection Modal ── */}
      <Dialog open={!!selectedRawLog} onOpenChange={(open) => { if (!open) setSelectedRawLog(null); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden">
          <DialogHeader className="p-4 border-b border-border/50">
            <DialogTitle className="font-heading text-base flex items-center gap-2">
              <Terminal className="h-4 w-4 text-primary" />
              Incoming Webhook Payload Inspector
            </DialogTitle>
            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
              {(selectedRawLog?.id || selectedRawLog?._id) && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(selectedRawLog.id || selectedRawLog._id);
                    toast.success("Log ID copied to clipboard");
                  }}
                  className="bg-secondary/60 hover:bg-secondary px-1.5 py-0.5 rounded text-[11px] text-foreground inline-flex items-center gap-1 cursor-pointer transition-colors"
                  title="Click to copy Log ID"
                >
                  <span>ID: {selectedRawLog.id || selectedRawLog._id}</span>
                  <Copy className="h-2.5 w-2.5 shrink-0" />
                </button>
              )}
              <span>Received {selectedRawLog?.createdAt ? new Date(selectedRawLog.createdAt).toLocaleString("en-IN") : ""} from {selectedRawLog?.clientIp}</span>
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 font-mono text-xs">
            <div className="grid grid-cols-2 gap-2 p-2 bg-secondary/30 rounded border border-border text-[11px]">
              <div><span className="text-muted-foreground">Provider:</span> {selectedRawLog?.provider?.toUpperCase()}</div>
              <div><span className="text-muted-foreground">Status:</span> {selectedRawLog?.status}</div>
              <div><span className="text-muted-foreground">Content-Type:</span> {selectedRawLog?.contentType || "application/json"}</div>
              <div><span className="text-muted-foreground">Punches Extracted:</span> {selectedRawLog?.punchesCount}</div>
            </div>

            {selectedRawLog?.error && (
              <div className="p-2.5 bg-destructive/10 border border-destructive/20 text-destructive rounded text-xs">
                <strong>Parse Error:</strong> {selectedRawLog.error}
              </div>
            )}

            <div>
              <Label className="text-xs font-heading font-semibold uppercase text-muted-foreground">Raw Request Body</Label>
              <pre className="mt-1 p-3 bg-secondary/40 border border-border rounded text-[11px] overflow-x-auto whitespace-pre-wrap select-all max-h-60">
                {selectedRawLog?.rawBody || "(Empty body)"}
              </pre>
            </div>

            {selectedRawLog?.punches?.length > 0 && (
              <div>
                <Label className="text-xs font-heading font-semibold uppercase text-muted-foreground">Extracted Punches ({selectedRawLog.punches.length})</Label>
                <pre className="mt-1 p-3 bg-secondary/20 border border-border rounded text-[11px] overflow-x-auto select-all max-h-40">
                  {JSON.stringify(selectedRawLog.punches, null, 2)}
                </pre>
              </div>
            )}
          </div>
          <div className="p-3 bg-secondary/20 border-t border-border/50 flex items-center justify-between">
            <Button
              variant="outline"
              size="sm"
              className="text-xs text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
              onClick={() => {
                setLogDeleteModal({ open: true, type: "single_raw", target: selectedRawLog });
              }}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete Payload
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={() => setSelectedRawLog(null)}
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Device User Mapping Modal ── */}
      <Dialog open={mappingModalOpen} onOpenChange={setMappingModalOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Map Device Code #{mappingCode}
              {mappingDeviceUserName ? ` (${mappingDeviceUserName})` : ""}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              Link physical biometric terminal user{" "}
              {mappingDeviceUserName ? (
                <>
                  <strong className="text-foreground">{mappingDeviceUserName}</strong> (code{" "}
                  <strong className="text-foreground font-mono">#{mappingCode}</strong>)
                </>
              ) : (
                <>
                  code <strong className="text-foreground font-mono">#{mappingCode}</strong>
                </>
              )}{" "}
              to an existing gym Member or Staff profile.
            </p>

            <div className="flex gap-2">
              <Button
                type="button"
                variant={mappingTargetType === "member" ? "default" : "outline"}
                size="sm"
                className="flex-1 text-xs rounded-sm font-heading"
                onClick={() => { setMappingTargetType("member"); setMappingTargetId(""); }}
              >
                Gym Member
              </Button>
              <Button
                type="button"
                variant={mappingTargetType === "staff" ? "default" : "outline"}
                size="sm"
                className="flex-1 text-xs rounded-sm font-heading"
                onClick={() => { setMappingTargetType("staff"); setMappingTargetId(""); }}
              >
                Staff / Coach
              </Button>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{mappingTargetType === "member" ? "Select Member *" : "Select Staff Member *"}</Label>
              <PersonSearchPicker
                key={`${mappingCode}-${mappingTargetType}`}
                kind={mappingTargetType}
                value={mappingTargetId}
                onChange={setMappingTargetId}
                initialQuery={mappingDeviceUserName}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs rounded-sm"
                onClick={() => setMappingModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="text-xs rounded-sm font-heading uppercase"
                onClick={handleSaveMapping}
                disabled={mappingSaving || !mappingTargetId}
              >
                {mappingSaving ? "Mapping..." : "Save & Sync to Device"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Extend Turnstile Access Modal ── */}
      <Dialog open={extendModalOpen} onOpenChange={setExtendModalOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CalendarIcon className="h-4 w-4 text-rose-400" />
              Extend Turnstile Access (Temporary Pass)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-xs text-muted-foreground">
              Temporarily allow turnstile access for expired/blocked members until a selected calendar date. This pushes the validity date directly to the physical terminal without altering billing memberships.
            </p>

            {/* Scope selection */}
            <div className="space-y-2">
              <Label className="text-xs font-medium">Target Members</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={extendScope === "all" ? "default" : "outline"}
                  size="sm"
                  className="flex-1 text-xs rounded-sm font-heading"
                  onClick={() => setExtendScope("all")}
                >
                  All Blocked ({blockedReconcileMembers.length})
                </Button>
                <Button
                  type="button"
                  variant={extendScope === "selected" ? "default" : "outline"}
                  size="sm"
                  className="flex-1 text-xs rounded-sm font-heading"
                  onClick={() => setExtendScope("selected")}
                >
                  Select Specific ({selectedExtendMemberIds.length})
                </Button>
              </div>
            </div>

            {/* If specific, show members checklist */}
            {extendScope === "selected" && (
              <div className="space-y-1.5 border border-border/60 rounded-sm p-2 max-h-40 overflow-y-auto bg-background/50">
                {blockedReconcileMembers.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic py-2 text-center">No blocked members found</p>
                ) : (
                  blockedReconcileMembers.map((m) => {
                    const isChecked = selectedExtendMemberIds.includes(m.entityId);
                    return (
                      <label
                        key={m.entityId || m.deviceCode}
                        className="flex items-center justify-between text-xs p-1.5 rounded hover:bg-secondary/20 cursor-pointer"
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedExtendMemberIds((prev) => [...prev, m.entityId]);
                              } else {
                                setSelectedExtendMemberIds((prev) => prev.filter((id) => id !== m.entityId));
                              }
                            }}
                            className="rounded border-border"
                          />
                          <span className="font-medium text-foreground">{m.name}</span>
                          {m.deviceCode && (
                            <Badge variant="outline" className="text-[10px] font-mono py-0 px-1">
                              #{m.deviceCode}
                            </Badge>
                          )}
                        </div>
                        <span className="text-[11px] text-muted-foreground font-mono">{m.phone || ""}</span>
                      </label>
                    );
                  })
                )}
              </div>
            )}

            {/* Quick date presets */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Quick Date Presets</Label>
              <div className="grid grid-cols-4 gap-1.5">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px] px-1"
                  onClick={() => handleSetQuickExtendDate(1)}
                >
                  +1 Day
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px] px-1"
                  onClick={() => handleSetQuickExtendDate(3)}
                >
                  +3 Days
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px] px-1"
                  onClick={() => handleSetQuickExtendDate(7)}
                >
                  +1 Week
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-[11px] px-1"
                  onClick={handleSetEndOfMonthDate}
                >
                  End Month
                </Button>
              </div>
            </div>

            {/* Date Picker Input */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Valid Until Date (Calendar) *</Label>
              <Input
                type="date"
                min={new Date().toISOString().split("T")[0]}
                value={extendDate}
                onChange={(e) => setExtendDate(e.target.value)}
                className="h-9 text-xs rounded-sm bg-background border-border font-mono"
              />
            </div>

            {/* Reason */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Extension Reason / Note</Label>
              <Input
                type="text"
                placeholder="e.g. Payment follow-up in progress"
                value={extendReason}
                onChange={(e) => setExtendReason(e.target.value)}
                className="h-8 text-xs rounded-sm bg-background border-border"
              />
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button
                variant="ghost"
                size="sm"
                className="text-xs rounded-sm"
                onClick={() => setExtendModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                className="text-xs rounded-sm font-heading uppercase bg-rose-600 hover:bg-rose-500 text-white"
                onClick={handleSaveExtendAccess}
                disabled={extendSubmitting || !extendDate || (extendScope === "selected" && selectedExtendMemberIds.length === 0)}
              >
                {extendSubmitting ? (
                  <>
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    Pushing to Device...
                  </>
                ) : (
                  <>
                    <Check className="h-3 w-3 mr-1" />
                    Save & Unlock Turnstile
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Biometric Sync Request & Response Debug Dialog */}
      <Dialog open={Boolean(debugSyncLog)} onOpenChange={(o) => { if (!o) setDebugSyncLog(null); }}>
        <DialogContent className="bg-card border-border max-w-2xl w-full">
          <DialogHeader className="pb-2">
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center justify-between text-sm">
              <span className="flex items-center gap-2">
                <Terminal className="h-4 w-4 text-primary" />
                Biometric Sync Debug Payload
              </span>
              {debugSyncLog && (
                <Badge
                  variant={debugSyncLog.success ? "default" : "destructive"}
                  className="text-[10px] font-mono"
                >
                  {debugSyncLog.success ? "200 SUCCESS" : "FAILED"}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground font-mono flex items-center gap-2 flex-wrap">
              {(debugSyncLog?.id || debugSyncLog?._id) && (
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(debugSyncLog.id || debugSyncLog._id);
                    toast.success("Log ID copied to clipboard");
                  }}
                  className="bg-secondary/60 hover:bg-secondary px-1.5 py-0.5 rounded text-[11px] text-foreground inline-flex items-center gap-1 cursor-pointer transition-colors"
                  title="Click to copy Log ID"
                >
                  <span>ID: {debugSyncLog.id || debugSyncLog._id}</span>
                  <Copy className="h-2.5 w-2.5 shrink-0" />
                </button>
              )}
              <span>Device: {debugSyncLog?.deviceName || debugSyncLog?.deviceId}</span>
              <span>•</span>
              <span>{debugSyncLog?.timestamp ? new Date(debugSyncLog.timestamp).toLocaleString() : "—"}</span>
            </DialogDescription>
          </DialogHeader>

          {debugSyncLog && (
            <div className="flex flex-col gap-4 max-h-[70vh] overflow-y-auto pr-1 py-1 text-xs">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="p-2 rounded border border-border bg-secondary/20">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase block">Action</span>
                  <span className="font-semibold">{debugSyncLog.action}</span>
                </div>
                <div className="p-2 rounded border border-border bg-secondary/20">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase block">Member</span>
                  <span className="font-semibold truncate block" title={debugSyncLog.memberName}>{debugSyncLog.memberName}</span>
                </div>
                <div className="p-2 rounded border border-border bg-secondary/20">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase block">Emp Code</span>
                  <span className="font-mono font-semibold">{debugSyncLog.empCode}</span>
                </div>
                <div className="p-2 rounded border border-border bg-secondary/20">
                  <span className="text-[10px] font-mono text-muted-foreground uppercase block">Expiry Sent</span>
                  <span className="font-mono font-semibold text-primary">{debugSyncLog.expiryTo}</span>
                </div>
              </div>

              {/* Request Payload */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground flex items-center gap-1.5">
                    <UploadCloud className="h-3.5 w-3.5 text-blue-500" />
                    Outgoing Request Payload
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] px-2 gap-1 text-muted-foreground"
                    onClick={() => {
                      const reqStr = JSON.stringify(
                        debugSyncLog.requestPayload || {
                          endpoint: debugSyncLog.apiUrl,
                          method: "POST (SOAP 1.1)",
                          action: debugSyncLog.action,
                          employeeCode: debugSyncLog.empCode,
                          memberName: debugSyncLog.memberName,
                          expiryTo: debugSyncLog.expiryTo,
                          isBlocked: debugSyncLog.isBlocked,
                        },
                        null,
                        2
                      );
                      navigator.clipboard.writeText(reqStr);
                      toast.success("Request payload copied to clipboard");
                    }}
                  >
                    <Copy className="h-3 w-3" /> Copy Request
                  </Button>
                </div>
                <pre className="p-3 rounded bg-zinc-950 text-zinc-100 font-mono text-[11px] overflow-x-auto border border-border/50 max-h-48 leading-relaxed whitespace-pre-wrap">
                  {JSON.stringify(
                    debugSyncLog.requestPayload || {
                      endpoint: debugSyncLog.apiUrl,
                      method: "POST (SOAP 1.1)",
                      action: debugSyncLog.action,
                      employeeCode: debugSyncLog.empCode,
                      memberName: debugSyncLog.memberName,
                      expiryTo: debugSyncLog.expiryTo,
                      isBlocked: debugSyncLog.isBlocked,
                    },
                    null,
                    2
                  )}
                </pre>
              </div>

              {/* Response Payload */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-foreground flex items-center gap-1.5">
                    <CloudDownload className="h-3.5 w-3.5 text-emerald-500" />
                    Incoming Response Payload
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] px-2 gap-1 text-muted-foreground"
                    onClick={() => {
                      const respStr = JSON.stringify(
                        debugSyncLog.responsePayload || {
                          success: debugSyncLog.success,
                          message: debugSyncLog.responseMessage,
                        },
                        null,
                        2
                      );
                      navigator.clipboard.writeText(respStr);
                      toast.success("Response payload copied to clipboard");
                    }}
                  >
                    <Copy className="h-3 w-3" /> Copy Response
                  </Button>
                </div>
                <pre className="p-3 rounded bg-zinc-950 text-zinc-100 font-mono text-[11px] overflow-x-auto border border-border/50 max-h-48 leading-relaxed whitespace-pre-wrap">
                  {JSON.stringify(
                    debugSyncLog.responsePayload || {
                      success: debugSyncLog.success,
                      message: debugSyncLog.responseMessage,
                    },
                    null,
                    2
                  )}
                </pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
